import React, { createContext, useContext, useState } from "react"
import { Col, Form, InputGroup, Row } from "react-bootstrap"
import { Alert, Button, Checkbox as AntCheckbox, DatePicker as AntDatePicker, Input, InputNumber, Popconfirm, Radio as AntRadio, Select as AntSelect, Space, Tooltip, message } from "antd"
import { CloseOutlined, ExclamationCircleOutlined, MinusCircleOutlined, MinusSquareOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import * as com from "../Common.js"
import AddressAutocomplete from "../Common/AddressAutocomplete"
import FormPreview from "../Common/components/FormPreview"
import CheckGroup from "../Common/components/CheckGroup"
import {
    assetsHaveIncompleteJointOwnership,
    isIncompleteJointOwnership,
    jointOthersSelected,
    liabilitiesHaveIncompleteJointOwnership,
    listHasIncompleteJointOwnership,
    normalizeOwnershipMode,
    ownershipPatch,
} from "../Interview/liabilityOwnershipV2"
import { buildCreditImportButtonMeta } from "../CreditReportV2/creditImportV2"

export {
    assetsHaveIncompleteJointOwnership,
    isIncompleteJointOwnership,
    liabilitiesHaveIncompleteJointOwnership,
    listHasIncompleteJointOwnership,
} from "../Interview/liabilityOwnershipV2"

const FormChromeContext = createContext({ editMode: true, showRequiredHints: false })

const CreditImportButton = ({
    sectionType,
    application,
    participantPartyId,
    reoIndex = 0,
    isSubjectProperty = false,
    hasAvailableCreditReport = false,
    creditReportCreatedAt = "",
    onOpen,
}) => {
    const { disabled, imported, tooltip } = buildCreditImportButtonMeta({
        sectionType,
        application,
        participantPartyId,
        reoIndex,
        isSubjectProperty,
        hasAvailableCreditReport,
        reportCreatedAt: creditReportCreatedAt,
    })
    const importType = sectionType === "section2c" ? "section2c" : "section3"
    const iconStroke = disabled ? "white" : "#325CEB"

    return (
        <Tooltip overlayClassName="import-credit-report-tooltip" title={tooltip} placement="bottomRight">
            <span>
                <Button
                    disabled={disabled}
                    className={`credit-report-button${imported ? " imported" : ""}`}
                    onClick={() => {
                        if (!disabled && onOpen) onOpen(importType)
                    }}
                >
                    Import from Credit Report
                    {imported ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M13.3332 7.33346C13.1701 6.16028 12.6259 5.07324 11.7843 4.23979C10.9426 3.40635 9.85034 2.87274 8.67562 2.72116C7.50089 2.56959 6.30892 2.80845 5.2833 3.40096C4.25769 3.99347 3.45534 4.90675 2.99984 6.00013M2.6665 3.33346V6.00013H5.33317M2.6665 8.66667C2.82954 9.83985 3.37379 10.9269 4.21541 11.7603C5.05703 12.5938 6.14933 13.1274 7.32406 13.279C8.49878 13.4305 9.69076 13.1917 10.7164 12.5992C11.742 12.0067 12.5443 11.0934 12.9998 10M13.3332 12.6667V10H10.6665" stroke={iconStroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M7.99935 4.00002H3.99935C3.64573 4.00002 3.30659 4.1405 3.05654 4.39054C2.80649 4.64059 2.66602 4.97973 2.66602 5.33335V12C2.66602 12.3536 2.80649 12.6928 3.05654 12.9428C3.30659 13.1929 3.64573 13.3334 3.99935 13.3334H10.666C11.0196 13.3334 11.3588 13.1929 11.6088 12.9428C11.8589 12.6928 11.9993 12.3536 11.9993 12V8.00002M7.33268 8.66669L13.3327 2.66669M13.3327 2.66669H9.99935M13.3327 2.66669V6.00002" stroke={iconStroke} strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </Button>
            </span>
        </Tooltip>
    )
}

export const FormChromeProvider = ({ editMode = true, showRequiredHints = false, children }) => (
    <FormChromeContext.Provider value={{ editMode: !!editMode, showRequiredHints: !!showRequiredHints }}>
        {children}
    </FormChromeContext.Provider>
)

export const useFormChrome = () => useContext(FormChromeContext)

const isBlank = (value) => value === "" || value === null || value === undefined

const PreviewText = ({ children }) => (
    <div style={{ fontSize: 16 }}>{children == null ? "" : children}</div>
)

const optionLabel = (options, value) => {
    const labelFor = (v) => {
        const selected = (options || []).find((item) => item.value === v)
        return selected ? selected.label : (v || "")
    }
    // Multi-select (e.g. Shared with) stores partyId[]; resolve each to display name.
    if (Array.isArray(value)) {
        return value.map(labelFor).filter(Boolean).join(", ")
    }
    return labelFor(value)
}

export const Field = ({ label, children }) => (
    <div>
        {label ? <div className="form1003-field-label">{label}</div> : null}
        {children}
    </div>
)

export const TextField = ({ label, value, onChange, placeholder, optional, onFocus, onBlur }) => {
    const { editMode, showRequiredHints } = useFormChrome()
    const warn = editMode && showRequiredHints && !optional && isBlank(value)
    if (!editMode) {
        return <FormPreview title={label} content={value || ""} />
    }
    return (
        <Field label={label}>
            <InputGroup>
                <Form.Control
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={placeholder || ""}
                    style={warn ? { border: "1px solid #faad14", borderRadius: "4px" } : {}}
                />
                {warn ? <div className="ExclamationCircleOutlined"><ExclamationCircleOutlined /></div> : null}
            </InputGroup>
        </Field>
    )
}

const prefixShouldWarn = (prefix, suffix) => {
    if (prefix === "$" || prefix === "Phone" || prefix === "Email") return true
    if (`${prefix || ""}`.toLowerCase().includes("phone")) return true
    if (prefix === "Email" || `${prefix || ""}`.toLowerCase() === "email") return true
    if (suffix === "Years" || suffix === "Months") return true
    return false
}

export const PrefixField = ({ label, prefix, suffix, value, onChange, placeholder, disabled, onFocus, onBlur, optional, error }) => {
    const { editMode, showRequiredHints } = useFormChrome()
    const warn = editMode && showRequiredHints && !optional && !disabled && prefixShouldWarn(prefix, suffix) && isBlank(value)
    if (!editMode) {
        let content = value || ""
        if (prefix === "$") content = `$ ${value || ""}`
        else if (suffix && !prefix) content = `${value || ""} ${suffix}`
        else if (prefix && suffix) content = `${prefix}: ${value || ""} ${suffix}`
        else if (prefix) content = `${prefix}: ${value || ""}`
        return <FormPreview title={label} content={content} />
    }
    return (
        <Field label={label}>
            <Input
                className="form1003-prefix-field"
                style={{ width: "100%" }}
                value={value || ""}
                onChange={(e) => onChange && onChange(e.target.value)}
                onFocus={onFocus}
                onBlur={onBlur}
                addonBefore={prefix}
                addonAfter={suffix}
                placeholder={placeholder || ""}
                size="large"
                disabled={disabled}
                status={error ? "error" : (warn ? "warning" : null)}
            />
            {error ? (
                <div style={{ color: "#ff4d4f", fontSize: 13, marginTop: 6, lineHeight: 1.4 }}>{error}</div>
            ) : null}
        </Field>
    )
}

/** Years/Months side-by-side at equal width (flex 1:1). Months clamped to 0–11 while typing. */
export const DurationFields = ({ years, months, onYearsChange, onMonthsChange }) => {
    const clampMonths = (value) => {
        if (value === null || typeof value === "undefined") return ""
        const digits = value.toString().replace(/\D/g, "")
        if (digits === "") return ""
        const n = parseInt(digits, 10)
        if (Number.isNaN(n)) return ""
        return String(Math.min(Math.max(n, 0), 11))
    }
    return (
        <div className="form1003-duration-fields">
            <div>
                <PrefixField suffix="Years" value={years} onChange={onYearsChange} />
            </div>
            <div>
                <PrefixField
                    suffix="Months"
                    value={months}
                    onChange={(v) => onMonthsChange && onMonthsChange(clampMonths(v))}
                />
            </div>
        </div>
    )
}

export const ChromeSelect = ({ options, value, onChange, optional, className, style, ...rest }) => {
    const { editMode, showRequiredHints } = useFormChrome()
    if (!editMode) {
        return <PreviewText>{optionLabel(options, value)}</PreviewText>
    }
    const warn = showRequiredHints && !optional && isBlank(value)
    return (
        <AntSelect
            {...rest}
            options={options}
            value={value}
            onChange={onChange}
            style={{ width: "100%", ...style }}
            className={[className, warn ? "select-required-empty" : ""].filter(Boolean).join(" ")}
        />
    )
}

export const SelectField = ({ label, value, options, onChange, optional, showSearch, optionFilterProp }) => {
    const { editMode } = useFormChrome()
    if (!editMode) {
        return <FormPreview title={label} content={optionLabel(options, value)} />
    }
    return (
        <Field label={label}>
            <ChromeSelect
                showSearch={showSearch}
                optionFilterProp={optionFilterProp}
                options={options}
                value={value}
                onChange={onChange}
                optional={optional}
            />
        </Field>
    )
}

export const ChromeRadioGroup = ({ value, onChange, options, children, optional, disabled, className }) => {
    const { editMode, showRequiredHints } = useFormChrome()
    if (!editMode) {
        return <PreviewText>{options ? optionLabel(options, value) : (value || "")}</PreviewText>
    }
    const warn = showRequiredHints && !optional && isBlank(value)
    return (
        <AntRadio.Group
            className={className}
            value={value}
            onChange={onChange}
            disabled={disabled}
            style={warn ? { border: "1px solid #faad14", padding: "11px 5px 0 5px", width: "100%" } : { width: "100%" }}
        >
            {children || (options || []).map((item) => (
                <AntRadio key={item.value} value={item.value}>{item.label}</AntRadio>
            ))}
        </AntRadio.Group>
    )
}

export const RadioGroupField = ({ label, value, onChange, options, children, optional, disabled }) => {
    const { editMode } = useFormChrome()
    if (!editMode && label) {
        return <FormPreview title={label} content={options ? optionLabel(options, value) : (value || "")} />
    }
    return (
        <Field label={label}>
            <ChromeRadioGroup value={value} onChange={onChange} options={options} optional={optional} disabled={disabled}>
                {children}
            </ChromeRadioGroup>
        </Field>
    )
}

/** Legacy-style horizontal/vertical pill radios (CheckGroup), used in Section 1a. */
export const SelectionField = ({ label, value, onChange, options, direction = "horizontal", optional, disabled }) => {
    const { editMode, showRequiredHints } = useFormChrome()
    const selected = (options || []).filter((o) => o.value === value)
    if (!editMode) {
        return <FormPreview title={label} content={selected.length > 0 ? selected[0].label : ""} />
    }
    const warn = showRequiredHints && !optional && isBlank(value)
    return (
        <div>
            {label ? <div className="form1003-field-label">{label}</div> : null}
            <div style={disabled ? { opacity: 0.55, pointerEvents: "none" } : undefined}>
                <CheckGroup
                    data={options}
                    onChangeSelect={disabled ? () => {} : onChange}
                    value={value}
                    type="radio"
                    style={{
                        width: "100%",
                        ...((value === "" || value === null) && warn && {
                            padding: "11px 5px 0px 5px",
                            border: "1px solid #faad14",
                        }),
                    }}
                    direction={direction}
                />
            </div>
        </div>
    )
}

export const YesNoField = ({ label, value, onChange }) => {
    const { editMode } = useFormChrome()
    const selected = YES_NO_OPTIONS.filter((o) => o.value === value)
    if (!editMode) {
        return (
            <div className="d-flex justify-content-between align-items-center form1003-yesno-row">
                <div className="mr-2 fontWeight500 col-7 pl-0">{label}</div>
                <div className="form1003-yesno-preview">{selected.length > 0 ? selected[0].label : ""}</div>
            </div>
        )
    }
    return (
        <div className="d-flex justify-content-between align-items-center form1003-yesno-row">
            <div className="mr-2 fontWeight500 col-7 pl-0">{label}</div>
            <CheckGroup
                data={YES_NO_OPTIONS}
                onChangeSelect={onChange}
                value={value}
                type="radio"
                style={{ width: "100%" }}
                direction="horizontal"
            />
        </div>
    )
}

export const ChromeDatePicker = ({ value, onChange, optional, ...rest }) => {
    const { editMode, showRequiredHints } = useFormChrome()
    const parsed = (() => {
        if (!value) return null
        if (dayjs.isDayjs?.(value)) return value
        const strict = dayjs(value, ["MM/DD/YYYY", "YYYY-MM-DD"], true)
        if (strict.isValid()) return strict
        const fallback = dayjs(value)
        return fallback.isValid() ? fallback : null
    })()
    if (!editMode) {
        return <PreviewText>{parsed ? parsed.format("MM/DD/YYYY") : (value || "")}</PreviewText>
    }
    const warn = showRequiredHints && !optional && isBlank(value)
    return (
        <AntDatePicker
            {...rest}
            size="large"
            style={{ width: "100%", ...(warn ? { border: "1px solid #faad14" } : {}) }}
            value={parsed}
            format={{ format: "MM/DD/YYYY", type: "mask" }}
            onChange={onChange}
        />
    )
}

export const DateField = ({ label, value, onChange, optional }) => {
    const { editMode } = useFormChrome()
    if (!editMode) {
        const parsed = value ? dayjs(value, ["MM/DD/YYYY", "YYYY-MM-DD"], true) : null
        const text = parsed?.isValid() ? parsed.format("MM/DD/YYYY") : (value || "")
        return <FormPreview title={label} content={text} />
    }
    return (
        <Field label={label}>
            <ChromeDatePicker value={value} onChange={(_, dateString) => onChange(dateString)} optional={optional} />
        </Field>
    )
}

export const ChromeCheckbox = ({ children, checked, onChange, className, optional, ...rest }) => {
    const { editMode } = useFormChrome()
    if (!editMode) {
        return checked ? <PreviewText>{children}</PreviewText> : null
    }
    return (
        <AntCheckbox className={className} checked={checked} onChange={onChange} {...rest}>
            {children}
        </AntCheckbox>
    )
}

const parseOccupationDate = (value) => {
    if (!value) return null
    const parsed = dayjs(value, ["MM/DD/YYYY", "YYYY-MM-DD"], true)
    if (parsed.isValid()) return parsed
    const fallback = dayjs(value)
    return fallback.isValid() ? fallback : null
}

const ownershipShareValue = (percentownership) => {
    if (percentownership === "less" || percentownership === "more") return percentownership
    return parseFloat(percentownership || 0) < 25 ? "less" : "more"
}

const canUseIncomeCalculation = (emp) => {
    if (!emp?.employername) return false
    if (!emp.selfemployed) return true
    const pct = emp.percentownership
    if (pct === "more") return true
    const n = parseFloat(pct)
    return !Number.isNaN(n) && n > 24
}

const IncomeCalcBtn = ({ emp, clipId, onClick }) => {
    const { editMode } = useFormChrome()
    if (!editMode) return null
    return (
    canUseIncomeCalculation(emp) ? (
        <span className="income-calculation-btn" style={{ cursor: "pointer" }} onClick={onClick}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <g clipPath={`url(#${clipId})`}>
                    <path d="M2.64435 1.3999V3.88879M1.3999 2.64435H3.88879M3.26657 10.111V12.5999M2.02212 11.3555H4.51101M7.62213 1.3999L9.04435 5.66657L12.5999 6.9999L9.04435 8.33324L7.62213 12.5999L6.1999 8.33324L2.64435 6.9999L6.1999 5.66657L7.62213 1.3999Z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                    <clipPath id={clipId}>
                        <rect width="14" height="14" fill="white"/>
                    </clipPath>
                </defs>
            </svg>
            Income calculation
        </span>
    ) : (
        <span disabled className="income-calculation-btn-disabled">Income calculation</span>
    )
    )
}

const canadian = new Set(["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"])
export const usStateOptions = Object.keys(com.mapAbbrFullName || {})
    .filter((abbr) => abbr.length === 2 && !canadian.has(abbr))
    .map((abbr) => ({ value: com.mapAbbrFullName[abbr], label: com.mapAbbrFullName[abbr] }))

export const stateOptionsFromLimits = (loanLimits, current) => {
    const keys = Object.keys(loanLimits || {})
    const options = keys.length
        ? [{ value: "", label: "..." }, ...keys.map((x) => ({ value: com.capitalize(x), label: com.capitalize(x) }))]
        : [{ value: "", label: "..." }, ...usStateOptions]
    if (current && !options.some((o) => o.value === current)) {
        options.push({ value: current, label: current })
    }
    return options
}

export const countyOptionsFromLimits = (loanLimits, state, current) => {
    const options = [{ value: "", label: "..." }]
    let key = `${state || ""}`.toUpperCase()
    if (key.length === 2) key = `${com.convertAbbStateToFull(key) || ""}`.toUpperCase()
    if (key && loanLimits && loanLimits[key]) {
        Object.keys(loanLimits[key]).forEach((x) => {
            options.push({ value: com.capitalize(x), label: com.capitalize(x) })
        })
    }
    if (current && !options.some((o) => o.value === current)) {
        options.push({ value: current, label: current })
    }
    return options
}

export const parseGooglePlace = (address) => {
    const num = com.getSubaddress(address, "street_number", "long_name")
    const route = com.getSubaddress(address, "route", "short_name")
    const city = com.getSubaddress(address, "locality", "long_name")
    const stateAbbr = com.getSubaddress(address, "administrative_area_level_1", "short_name")
    const zip = com.getSubaddress(address, "postal_code", "long_name")
    const county = com.getSubaddress(address, "administrative_area_level_2", "long_name")
    return {
        street: `${num} ${route}`.trim(),
        city,
        stateAbbr,
        state: com.capitalize(com.convertAbbStateToFull(stateAbbr)),
        zip,
        county,
    }
}

export const StateSelect = ({ label, value, onChange, loanLimits, optional }) => (
    <SelectField
        label={label}
        value={value || ""}
        options={stateOptionsFromLimits(loanLimits, value)}
        onChange={onChange}
        optional={optional}
        showSearch
        optionFilterProp="label"
    />
)

export const CountySelect = ({ label, value, onChange, state, loanLimits, optional }) => (
    <SelectField
        label={label}
        value={value || ""}
        options={countyOptionsFromLimits(loanLimits, state, value)}
        onChange={onChange}
        optional={optional}
        showSearch
        optionFilterProp="label"
    />
)

// Structured V2 address editor. The street label is supplied by each caller because
// 1003 sections use different business labels (for example, Address vs Mailing Address).
export const StructuredAddressField = ({ id, label, address = {}, onChange, onPlace, loanLimits, optional }) => (
    <>
        <Row className="mb-1 form1003-address-layout">
            <Col sm={9}>
                <StreetAddressField id={id} label={label} value={address.street} onChange={(v) => onChange("street", v)} onPlace={onPlace} optional={optional} />
            </Col>
            <Col sm={3}><TextField label="Unit" value={address.unit} onChange={(v) => onChange("unit", v)} optional /></Col>
        </Row>
        <Row className="form1003-address-layout">
            <Col sm={3} className="mb-4"><StateSelect label="State" value={address.state} onChange={(v) => onChange("state", v)} loanLimits={loanLimits} optional={optional} /></Col>
            {/* County is informational and is not required by the 1003 V2 address fields. */}
            <Col sm={3} className="mb-4"><CountySelect label="County" value={address.county} state={address.state} onChange={(v) => onChange("county", v)} loanLimits={loanLimits} optional /></Col>
            <Col sm={3} className="mb-4"><TextField label="City" value={address.city} onChange={(v) => onChange("city", v)} optional={optional} /></Col>
            <Col sm={3} className="mb-4"><TextField label="Zip" value={address.zip} onChange={(v) => onChange("zip", v)} optional={optional} /></Col>
        </Row>
    </>
)

export const StreetAddressField = ({ id, label, value, onChange, onPlace, optional }) => {
    const { editMode, showRequiredHints } = useFormChrome()
    if (!editMode) {
        return <FormPreview title={label || "Address"} content={value || ""} />
    }
    const warn = showRequiredHints && !optional && isBlank(value)
    return (
        <Field label={label}>
            <AddressAutocomplete
                id={id}
                label={null}
                border={warn}
                style={warn ? { border: "1px solid #faad14" } : undefined}
                defaultValue={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onCompletion={onPlace ? (addr) => onPlace(parseGooglePlace(addr)) : undefined}
                placeholder="Please choose from the suggested addresses generated based on the input."
                size="md"
            />
        </Field>
    )
}

const YES_NO_OPTIONS = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
]

const YesNoChoice = ({ label, value, onChange }) => {
    const { editMode } = useFormChrome()
    const normalized = value === "yes" ? "yes" : "no"
    return (
        <div className="d-flex justify-content-between align-items-center form1003-yesno-row">
            <div className="mr-2 fontWeight500 col-7 pl-0">{label}</div>
            {editMode ? (
                <CheckGroup
                    data={YES_NO_OPTIONS}
                    onChangeSelect={onChange}
                    value={normalized}
                    type="radio"
                    style={{ width: "100%" }}
                    direction="horizontal"
                />
            ) : (
                <div className="form1003-yesno-preview">{normalized === "yes" ? "Yes" : "No"}</div>
            )}
        </div>
    )
}

const YesNo = ({ label, value, onChange }) => {
    // Match legacy Interview1003View 5a/5b: empty declaration answers display as "no".
    const normalized = value === "yes" ? "yes" : "no"
    return <YesNoChoice label={label} value={normalized} onChange={onChange} />
}

const YesNoBool = ({ question, value, onChange }) => {
    const { editMode } = useFormChrome()
    const radioValue = value === true ? "yes" : value === false ? "no" : ""
    if (!editMode) {
        return (
            <div className="d-flex justify-content-between align-items-center form1003-yesno-row">
                <div className="mr-2 fontWeight500 col-7 pl-0">{question}</div>
                <div className="form1003-yesno-preview">
                    {radioValue === "yes" ? "Yes" : radioValue === "no" ? "No" : ""}
                </div>
            </div>
        )
    }
    return (
        <div className="d-flex justify-content-between align-items-center form1003-yesno-row">
            <div className="mr-2 fontWeight500 col-7 pl-0">{question}</div>
            <CheckGroup
                data={YES_NO_OPTIONS}
                onChangeSelect={(v) => onChange(v === "yes")}
                value={radioValue}
                type="radio"
                style={{ width: "100%" }}
                direction="horizontal"
            />
        </div>
    )
}

const MoneyPctField = ({ label, amount, propertyValue, onAmount }) => {
    const { editMode } = useFormChrome()
    const n = numOrZero(amount)
    const pv = numOrZero(propertyValue)
    const pct = pv > 0 ? Math.round((n / pv) * 10000) / 100 : 0
    if (!editMode) {
        return <FormPreview title={label} content={`$ ${n} (${pct}%)`} />
    }
    return (
        <>
            <div className="insurance-and-tax-breakdown-label">{label}</div>
            <Space.Compact style={{ width: "100%" }}>
                <InputNumber
                    prefix="$"
                    className="estimated-monthly-payment-input"
                    style={{ flex: 1 }}
                    controls={false}
                    stringMode
                    value={n === 0 ? "0" : String(n)}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(v) => moneyTypingValue(v, 2)}
                    onChange={(v) => onAmount(v == null || v === "" ? "" : moneyTypingValue(v, 2))}
                />
                <InputNumber
                    suffix="%"
                    className="estimated-monthly-payment-input"
                    style={{ width: 74 }}
                    min={0}
                    max={100}
                    step={0.01}
                    controls={false}
                    value={pct}
                    onChange={(v) => {
                        if (v === null || v === undefined) return
                        onAmount(dollarValue(String((v / 100) * pv)))
                    }}
                />
            </Space.Compact>
        </>
    )
}

const AddLink = ({ onClick, children }) => {
    const { editMode } = useFormChrome()
    if (!editMode) return null
    return (
        <div
            style={{ fontSize: 16, fontWeight: 500, color: "#325CEB", cursor: "pointer" }}
            onClick={onClick}
        >
            <img src="/images/bluePlusInCircleMark.png" style={{ width: 20, height: 20 }} className="ml-4 mr-2 my-3" alt="" />
            {children}
        </div>
    )
}

const DeleteControl = ({ onConfirm, title = "Sure to delete?", square, confirm = true }) => {
    const { editMode } = useFormChrome()
    if (!editMode) return null
    const Icon = square ? MinusSquareOutlined : MinusCircleOutlined
    const icon = <Icon style={{ color: "#325CEB", fontSize: 16, cursor: "pointer" }} onClick={confirm ? undefined : onConfirm} />
    if (!confirm) return icon
    return (
        <Popconfirm title={title} onConfirm={onConfirm}>
            {icon}
        </Popconfirm>
    )
}

const SectionIntro = ({ id, title, text }) => (
    <div id={id} className="p-4">
        <div className="fontWeight500" style={{ fontSize: 20 }}>{title}</div>
        <div>{text}</div>
    </div>
)

const withCurrentOption = (options, current) => {
    if (current && !options.some((o) => o.value === current)) {
        return [...options, { value: current, label: current }]
    }
    return options
}

/** Avoid IEEE float tails like 3294.4700000000003 in Total income. */
export const formatMonthlyMoney = (n) => {
    if (!Number.isFinite(n)) return ""
    const cents = Math.round(n * 100)
    if (cents % 100 === 0) return String(cents / 100)
    const sign = cents < 0 ? "-" : ""
    const abs = Math.abs(cents)
    return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`
}

/** Show employment income as monthly; preserve typing drafts when already monthly. */
export const displayMonthlyIncome = (income, key) => {
    const raw = income?.[key]
    if (raw === "" || raw == null) return ""
    const period = `${income?.[`${key}monthly`] || ""}`.toLowerCase()
    // Only convert when explicitly yearly; anything else keep raw so drafts like "1200." survive.
    if (period !== "yearly") return String(raw)
    const n = parseFloat(raw)
    if (Number.isNaN(n)) return String(raw)
    const monthly = n / 12
    return Number.isInteger(monthly) ? String(monthly) : String(Math.round(monthly * 100) / 100)
}

export const sumMonthlyIncomeFields = (income) => {
    const total = ["base", "overtime", "bonuses", "commissions", "military", "other"].reduce((sum, key) => {
        const n = parseFloat(displayMonthlyIncome(income, key))
        return sum + (Number.isNaN(n) ? 0 : n)
    }, 0)
    return formatMonthlyMoney(total)
}

/** LOS 1003 V2 shows monthly; borrower-side may store yearly in incomeorlossperiod. */
export const displayMonthlyIncomeOrLoss = (emp) => {
    const raw = emp?.incomeorloss
    if (raw === "" || raw == null) return ""
    const period = `${emp?.incomeorlossperiod || ""}`.toLowerCase()
    // Monthly (or unset/other): keep the stored string so in-progress drafts like "1200." survive typing.
    if (period !== "yearly") {
        return String(raw)
    }
    const v = parseFloat(String(raw).replace(/,/g, ""))
    if (Number.isNaN(v)) return String(raw)
    return formatMonthlyMoney(v / 12)
}

const dollarValue = (value) => {
    if (value === null || typeof value === "undefined") return ""
    const cleaned = value.toString().replace(/[$,\s]/g, "")
    const [integerPart = "", ...decimalParts] = cleaned.split(".")
    const integer = integerPart.replace(/\D/g, "")
    const decimal = decimalParts.join("").replace(/\D/g, "").slice(0, 2)
    if (integer === "" && decimal === "") return ""
    if (decimal !== "") return `${integer || "0"}.${decimal}`
    return integer
}

/** Preserve in-progress decimals (e.g. "100.") while typing; strip letters and extra dots. */
export const moneyTypingValue = (value, maxDecimalPlaces = 2, allowNegative = false) => {
    if (value === null || typeof value === "undefined") return ""
    // Normalize fullwidth / ideographic decimal separators (common with CJK keyboards).
    const raw = value.toString().replace(/[$,\s]/g, "").replace(/[。．]/g, ".")
    if (allowNegative && raw.trim() === "-") return "-"
    const negative = allowNegative && raw.trim().startsWith("-")
    let cleaned = raw.replace(/[^\d.]/g, "")
    const firstDot = cleaned.indexOf(".")
    if (firstDot !== -1) {
        cleaned =
            cleaned.slice(0, firstDot + 1) +
            cleaned.slice(firstDot + 1).replace(/\./g, "")
    }
    if (firstDot === -1) {
        if (!cleaned) return negative ? "-" : ""
        return `${negative ? "-" : ""}${cleaned}`
    }
    const [intPart = "", ...rest] = cleaned.split(".")
    const decimalRaw = rest.join("") || ""
    const decimal =
        maxDecimalPlaces == null
            ? decimalRaw
            : decimalRaw.slice(0, maxDecimalPlaces)
    if (decimal === "" && raw.replace(/^-/, "").endsWith(".")) {
        return `${negative ? "-" : ""}${intPart || "0"}.`
    }
    if (decimal === "") {
        if (!intPart) return negative ? "-" : ""
        return `${negative ? "-" : ""}${intPart}`
    }
    return `${negative ? "-" : ""}${intPart || "0"}.${decimal}`
}

/**
 * Money field that keeps a local draft while focused so trailing "." (e.g. "1200.")
 * stays visible even if parent display reformats yearly↔monthly mid-keystroke.
 */
export const MoneyPrefixField = ({
    value,
    onChange,
    maxDecimalPlaces = 2,
    allowNegative = true,
    onBlur,
    ...rest
}) => {
    const [draft, setDraft] = useState(null)
    const shown = draft !== null ? draft : (value ?? "")
    return (
        <PrefixField
            {...rest}
            value={shown}
            onFocus={() => setDraft(value == null || value === "" ? "" : String(value))}
            onChange={(v) => {
                const next = moneyTypingValue(v, maxDecimalPlaces, allowNegative)
                setDraft(next)
                onChange && onChange(next)
            }}
            onBlur={(e) => {
                setDraft(null)
                onBlur && onBlur(e)
            }}
        />
    )
}

export const MoneyTextField = ({
    value,
    onChange,
    maxDecimalPlaces = 2,
    allowNegative = true,
    onBlur,
    ...rest
}) => {
    const [draft, setDraft] = useState(null)
    const shown = draft !== null ? draft : (value ?? "")
    return (
        <TextField
            {...rest}
            value={shown}
            onFocus={() => setDraft(value == null || value === "" ? "" : String(value))}
            onChange={(v) => {
                const next = moneyTypingValue(v, maxDecimalPlaces, allowNegative)
                setDraft(next)
                onChange && onChange(next)
            }}
            onBlur={(e) => {
                setDraft(null)
                onBlur && onBlur(e)
            }}
        />
    )
}

/** Section 2a/2b/2c/2d/4a/4b/4c/4d amounts only — matches legacy table typing; optional blur normalize. */
const SectionAmountField = ({
    label,
    prefix,
    suffix,
    value,
    onChange,
    normalizeOnBlur,
    unlimitedDecimals = false,
    optional,
    disabled,
    error,
    onBlur,
}) => {
    const maxDecimalPlaces = unlimitedDecimals ? null : 2
    const handleBlur = (e) => {
        if (onChange && normalizeOnBlur) {
            const normalized = dollarValue(value)
            if (normalized !== (value ?? "")) {
                onChange(normalized)
            }
        }
        onBlur && onBlur(e)
    }
    return (
        <PrefixField
            label={label}
            prefix={prefix}
            suffix={suffix}
            value={value}
            optional={optional}
            disabled={disabled}
            error={error}
            onChange={(v) =>
                onChange && onChange(moneyTypingValue(v, maxDecimalPlaces))
            }
            onBlur={handleBlur}
        />
    )
}

const accountTypeOptions = (com.bankaccounttypes || []).map(([value, label]) => ({ value, label }))
const otherAssetTypeOptions = (com.otherassetsandcredits || []).map(([value, label]) => ({ value, label }))
const liabilityTypeOptions = [
    { value: "Installment", label: "Installment" },
    { value: "Mortgage", label: "Lease" },
    { value: "Open", label: "Open 30-Day" },
    { value: "Revolving", label: "Revolving" },
    { value: "Unknown", label: "Other" },
]
const ownershipModeOptions = [
    { value: "Individual", label: "Individual" },
    { value: "Joint", label: "Joint" },
]
const paidOffOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
]
const expenseTypeOptions = [
    { value: "alimony", label: "Alimony" },
    { value: "childsupport", label: "Child Support" },
    { value: "maintenancepayments", label: "Separate Maintenance" },
    { value: "jobrelatedexpenses", label: "Job Related Expenses" },
    { value: "other", label: "Other" },
]

const participantDisplayName = (person) => {
    const name = [person?.firstName, person?.middleName, person?.lastName]
        .map((part) => `${part || ""}`.trim())
        .filter(Boolean)
        .join(" ")
    return name || "Applicant"
}

const NON_OWN_SHARED_OWNER_WARNING_KEY = "losV2NonOwnSharedOwnerWarningDismissed"

export const isNonOwnHousingStatus = (ownership) => {
    const v = `${ownership || ""}`.trim().toLowerCase()
    return Boolean(v) && v !== "own"
}

/** REO rows on other parties' lists where this party is listed in ownerPartyIds. */
export const listSharedPropertiesForParty = (application, partyId) => {
    if (!partyId) return []
    return (application?.participantOrder || []).flatMap((id) => {
        if (!id || id === partyId) return []
        return (application?.finance?.realEstateByPartyId?.[id]?.properties || [])
            .filter((item) => (item.ownerPartyIds || []).includes(partyId))
            .map((item) => ({ ...item, homePartyId: item.homePartyId || id }))
    })
}

/** True when shared REO is the same location as the party's present residence. */
export const isSharedPropertyAtPresentResidence = (item, presentAddress) => {
    const street = `${presentAddress?.street || presentAddress?.address || ""}`.trim()
    if (!street) return false
    return ownedLocationKey(presentAddress) === ownedLocationKey(item)
}

/**
 * Soft warning only when housing is not Own and the party is co-owner of a
 * shared property at their current residential address (not other addresses).
 */
export const hasNonOwnSharedOwnerConflict = (application, participant) => {
    if (!participant?.partyId || !isNonOwnHousingStatus(participant.residenceOwnership)) return false
    return listSharedPropertiesForParty(application, participant.partyId)
        .some((item) => isSharedPropertyAtPresentResidence(item, participant.presentAddress))
}

export const shouldShowNonOwnSharedOwnerWarningOnProperty = (participant, item) => {
    if (!participant || !item || !isNonOwnHousingStatus(participant.residenceOwnership)) return false
    return isSharedPropertyAtPresentResidence(item, participant.presentAddress)
}

const nonOwnSharedOwnerDismissKey = (loanId, partyId) => `${loanId || ""}:${partyId || ""}`

export const isNonOwnSharedOwnerWarningDismissed = (loanId, partyId) => {
    if (!loanId || !partyId) return false
    try {
        const raw = localStorage.getItem(NON_OWN_SHARED_OWNER_WARNING_KEY)
        const map = raw ? JSON.parse(raw) : {}
        return Boolean(map[nonOwnSharedOwnerDismissKey(loanId, partyId)])
    } catch {
        return false
    }
}

export const dismissNonOwnSharedOwnerWarning = (loanId, partyId) => {
    if (!loanId || !partyId) return
    try {
        const raw = localStorage.getItem(NON_OWN_SHARED_OWNER_WARNING_KEY)
        const map = raw ? JSON.parse(raw) : {}
        map[nonOwnSharedOwnerDismissKey(loanId, partyId)] = true
        localStorage.setItem(NON_OWN_SHARED_OWNER_WARNING_KEY, JSON.stringify(map))
    } catch {
        // ignore quota / private mode
    }
}

export const nonOwnSharedOwnerWarningText = (personName, scope = "property") => {
    const name = `${personName || ""}`.trim() || "This applicant"
    if (scope === "section") {
        return `${name}'s housing status is not Own, but their current address matches a property shared with them. Please confirm this is correct.`
    }
    return `${name}'s housing status is not Own, but this property is shared with them. Please confirm this is correct.`
}

export const NonOwnSharedOwnerWarningBanner = ({ message, onDismiss }) => (
    <div className="v2-soft-warning-banner" role="status">
        <div className="v2-soft-warning-banner-text">{message}</div>
        <button
            type="button"
            className="v2-soft-warning-banner-dismiss"
            onClick={onDismiss}
            aria-label="Dismiss"
        >
            <CloseOutlined />
        </button>
    </div>
)

/** Content-aligned Ownership: Individual | Joint + Shared with (ownerPartyIds). */
const OwnershipFields = ({
    currentPartyId,
    ownership,
    ownerPartyIds,
    otherParticipants,
    onChange,
    disabled = false,
    layout = "default",
}) => {
    const mode = normalizeOwnershipMode(ownership, ownerPartyIds)
    const selectedOthers = jointOthersSelected(ownerPartyIds, currentPartyId)
    const defaultOtherIds = otherParticipants.length === 1
        ? [otherParticipants[0].partyId].filter(Boolean)
        : []
    const isReo = layout === "reo"
    return (
        <>
            <Col sm={isReo ? 6 : undefined} className={isReo ? "mb-3 form1003-col-half" : "mb-3"}>
                <Field label="Ownership">
                    <ChromeSelect
                        style={{ width: "100%" }}
                        value={mode}
                        options={ownershipModeOptions}
                        disabled={disabled}
                        onChange={(v) => {
                            if (disabled) return
                            if (v === "Joint") {
                                // Classic 2-party loan: default Shared with to the only coborrower.
                                // Multi coborrower: leave empty until user picks (defer autosave).
                                const others = selectedOthers.length ? selectedOthers : defaultOtherIds
                                onChange(ownershipPatch(currentPartyId, "Joint", others))
                                return
                            }
                            onChange(ownershipPatch(currentPartyId, "Individual", []))
                        }}
                    />
                </Field>
            </Col>
            {mode === "Joint" ? (
                <Col sm={isReo ? 6 : undefined} className={isReo ? "mb-3 form1003-col-half" : "mb-3 form1003-col-full"}>
                    <Field label="Shared with">
                        <ChromeSelect
                            mode="multiple"
                            allowClear={!disabled}
                            disabled={disabled}
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select applicants"
                            style={{ width: "100%" }}
                            value={selectedOthers}
                            status={!disabled && selectedOthers.length === 0 ? "error" : undefined}
                            options={otherParticipants.map((person) => ({
                                value: person.partyId,
                                label: participantDisplayName(person),
                            }))}
                            onChange={(ids) => {
                                if (disabled) return
                                onChange(ownershipPatch(currentPartyId, "Joint", ids || []))
                            }}
                        />
                        {!disabled && selectedOthers.length === 0 ? (
                            <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
                                Select at least one other applicant to share with.
                            </div>
                        ) : null}
                    </Field>
                </Col>
            ) : null}
        </>
    )
}

const emptyJob = () => ({
    hasoccupation: "",
    employername: "",
    employeraddress: "",
    employerstreet: "",
    employerunit: "",
    employercity: "",
    employercounty: "",
    employerstate: "",
    employerzip: "",
    from: "",
    positiontitle: "",
    typeofbusiness: "",
    phone: "",
    yearsatlineofwork: "",
    monthsatlineofwork: "",
    selfemployed: false,
    employedbyagent: false,
    percentownership: "",
    incomeorloss: "",
    incomeorlossperiod: "monthly",
    income: {
        base: "",
        basemonthly: "monthly",
        overtime: "",
        overtimemonthly: "monthly",
        bonuses: "",
        bonusesmonthly: "monthly",
        commissions: "",
        commissionsmonthly: "monthly",
        military: "",
        militarymonthly: "monthly",
        other: "",
        othermonthly: "monthly",
    },
    key: `job-${Date.now()}-${Math.random().toString(16).slice(2)}`,
})

const emptyOtherIncome = () => ({
    key: `inc-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    incometype: "Other",
    description: "",
    amount: "",
    period: "monthly",
})

const emptyPrevJob = () => ({
    employername: "",
    employeraddress: "",
    employerstreet: "",
    employerunit: "",
    employercity: "",
    employercounty: "",
    employerstate: "",
    employerzip: "",
    from: "",
    to: "",
    positiontitle: "",
    typeofbusiness: "",
    phone: "",
    income: "",
    incomeperiod: "monthly",
    selfemployed: false,
    employedbyagent: false,
    key: `prev-${Date.now()}-${Math.random().toString(16).slice(2)}`,
})

const emptyAccount = (partyId = "") => ({
    key: `acct-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    bankName: "",
    account: "",
    accountType: "CheckingAccount",
    balance: "",
    ownership: "Individual",
    ownerPartyIds: partyId ? [partyId] : [],
    isRetirement: false,
})

const emptyOtherAsset = (partyId = "") => ({
    key: `oa-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    description: "",
    assetType: "",
    value: "",
    ownership: "Individual",
    ownerPartyIds: partyId ? [partyId] : [],
})

const emptyLoan = (partyId = "") => ({
    key: `loan-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    creditLoanType: "",
    creditLiabilityAccountType: "",
    name: "",
    accountNumber: "",
    monthlyPayment: "",
    remainingBalance: "",
    paidOffAtClosing: "",
    ownership: "Individual",
    ownerPartyIds: partyId ? [partyId] : [],
    propertyAddress: "",
    forSubjectProperty: false,
})

const emptyExpense = () => ({
    key: `exp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    amount: "",
    owedto: "",
    alimonyorsupport: "alimony",
})

const emptyOwned = (partyId = "") => ({
    key: `reo-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    address: "",
    street: "",
    unit: "",
    city: "",
    county: "",
    state: "",
    zip: "",
    zipcode: "",
    ownedFreeAndClear: true,
    // Same default as the server's current-residence row. "pending" made DTI ignore the
    // property's rent (and drop it entirely once a sale contract was ticked) unless changed.
    status: "retained",
    occupancy: "principal",
    propertyType: "singlefamily",
    marketValue: "",
    liens: "",
    grossRentalIncome: "",
    insuranceMaintenanceTaxes: "",
    insurancemain: "",
    insurancetax: "",
    insurancehoa: "",
    netRentalIncome: "",
    loans: [],
    ownership: "Individual",
    ownerPartyIds: partyId ? [partyId] : [],
    homePartyId: partyId || "",
})

const emptyGift = (partyId = "") => ({
    key: `gift-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    assetType: "",
    value: "",
    source: "",
    sourceDescription: "",
    deposited: true,
    ownership: "Individual",
    ownerPartyIds: partyId ? [partyId] : [],
})

const emptyMortgage = () => ({
    key: `nm-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    creditor: "",
    lienType: "firstlien",
    monthlyAmount: "",
    loanAmount: "",
    creditLimit: "",
})

const propertyAddressText = (item) => `${item?.address || item?.street || ""}`.trim()

const ownedLocationKey = (item = {}) => [
    item.street || item.address || "",
    item.unit || "",
    item.city || "",
    item.state || "",
    item.zip || item.zipcode || "",
].map((part) => `${part}`.trim().toLowerCase()).join("|")

const shouldShowCurrentResidenceOwned = (ownership, present, subject, isPurchaseLoan) => {
    if (`${ownership || ""}`.toLowerCase() !== "own") return false
    const street = `${present?.street || present?.address || ""}`.trim()
    if (!street) return false
    if (isPurchaseLoan) return true
    const subjectStreet = `${subject?.address || subject?.street || ""}`.trim()
    if (!subjectStreet) return true
    return ownedLocationKey(present) !== ownedLocationKey({
        street: subject.address || subject.street,
        unit: subject.unit,
        city: subject.city,
        state: subject.state,
        zip: subject.zipcode || subject.zip,
    })
}

const isPartyCurrentResidenceRow = (item, partyId, presentKey) => {
    const key = `${item?.key || ""}`
    if (partyId && key === `current-residence:${partyId}`) return true
    if (key === "current-residence" && (!item?.homePartyId || item.homePartyId === partyId)) return true
    // Address match only when caller supplies presentKey (Own housing). Rent/RentFree
    // must not hide Shared-with-you / other REO that happen to share the street.
    return Boolean(presentKey) && ownedLocationKey(item) === presentKey
}

const assemblePartyOwnedProperties = ({ stored, partyId, present, ownership, subject, isPurchaseLoan }) => {
    const includeCurrent = shouldShowCurrentResidenceOwned(ownership, present, subject, isPurchaseLoan)
    const street = `${present?.street || present?.address || ""}`.trim()
    // Only Own uses address dedupe; otherwise keep non-current-residence rows visible.
    const presentKey = includeCurrent && street ? ownedLocationKey(present) : ""
    const rest = []
    let current = null
    for (const item of stored || []) {
        if (isPartyCurrentResidenceRow(item, partyId, presentKey)) {
            if (!current) current = item
            continue
        }
        rest.push(item)
    }
    if (!includeCurrent) return rest
    if (!current) return rest
    return [{ ...current, isCurrent: true }, ...rest]
}

// rentalIncomeApplies is the rule section 3's rental income field is shown by:
// an investment property or a two-to-four unit one, and nothing else.
const rentalIncomeApplies = (occupancy, propertyType) => (
    occupancy === "investment" || propertyType === "twotofour" // || propertyType === "twotofourfamily"
)

const loanCopyForProperty = (item, address) => ({
    ...item,
    propertyAddress: address,
    // Property/subject mortgage tables use loan product type, not 2c "Mortgage" (Lease).
    creditLoanType: "ConventionalRealEstateMortgage",
})

const loanKeyMatches = (loanKey, targetKey) => {
    const a = `${loanKey || ""}`
    const b = `${targetKey || ""}`
    if (!a || !b) return false
    if (a === b) return true
    const strip = (k) => k.replace(/^(subject-mortgage:|property-mortgage:)/, "")
    return strip(a) === strip(b)
}

const stripLoanFromList = (list, loanKey) => (list || []).filter((loan) => !loanKeyMatches(loan.key, loanKey))

const emptyEO = () => ({
    ethnicity: "",
    sex: "",
    hispanic: { mexican: false, puertorican: false, cuban: false, other: false, otherdesctiption: "" },
    race: {
        notfurnished: false, asian: false, pacific: false, black: false, americanindian: false, white: false,
        indiantribe: "",
        asiandetails: {},
        pacificdetails: {},
    },
})

const incomeTypes = (com.othersouresofincome || []).map((row) => ({ value: row[0], label: row[1] }))

const v2AnchorItemsCompact = [
    { key: "s1", href: "#s1", title: "S1", children: [
        { key: "1a", href: "#1a", title: "1a" },
        { key: "1b", href: "#1b", title: "1b" },
        { key: "1c", href: "#1c", title: "1c" },
        { key: "1d", href: "#1d", title: "1d" },
        { key: "1e", href: "#1e", title: "1e" },
    ]},
    { key: "s2", href: "#s2", title: "S2", children: [
        { key: "2a", href: "#2a", title: "2a" },
        { key: "2b", href: "#2b", title: "2b" },
        { key: "2c", href: "#2c", title: "2c" },
        { key: "2d", href: "#2d", title: "2d" },
    ]},
    { key: "s3", href: "#s3", title: "S3", children: [
        { key: "3a", href: "#3a", title: "3a" },
        { key: "3b", href: "#3b", title: "3b" },
        { key: "3c", href: "#3c", title: "3c" },
    ]},
    { key: "s4", href: "#s4", title: "S4", children: [
        { key: "4a", href: "#4a", title: "4a" },
        { key: "4b", href: "#4b", title: "4b" },
        { key: "4c", href: "#4c", title: "4c" },
        { key: "4d", href: "#4d", title: "4d" },
    ]},
    { key: "s5", href: "#s5", title: "S5", children: [
        { key: "5a", href: "#5a", title: "5a" },
        { key: "5b", href: "#5b", title: "5b" },
    ]},
    { key: "s6", href: "#s6", title: "S6" },
    { key: "s7", href: "#s7", title: "S7" },
    { key: "s8", href: "#s8", title: "S8" },
    { key: "s9", href: "#s9", title: "S9" },
]

/** Full titles for Borrower 1003 tab (match legacy Interview1003View menuItems). */
const v2AnchorItemsFull = [
    { key: "s1", href: "#s1", title: "S1. Borrower Information", children: [
        { key: "1a", href: "#1a", title: "1a. Personal Information" },
        { key: "1b", href: "#1b", title: "1b. Current Employment/Self-Employment and Income" },
        { key: "1c", href: "#1c", title: "1c. Additional Employment/Self-Employment and Income" },
        { key: "1d", href: "#1d", title: "1d. Previous Employment/Self-Employment and Income" },
        { key: "1e", href: "#1e", title: "1e. Income from Other Sources" },
    ]},
    { key: "s2", href: "#s2", title: "S2. Financial Information", children: [
        { key: "2a", href: "#2a", title: "2a. Assets" },
        { key: "2b", href: "#2b", title: "2b. Other Assets and Credits You Have" },
        { key: "2c", href: "#2c", title: "2c. Liabilities – Credit Cards, Other Debts, and Leases that You Owe" },
        { key: "2d", href: "#2d", title: "2d. Other Liabilities and Expenses" },
    ]},
    { key: "s3", href: "#s3", title: "S3. Financial Information — Real Estate", children: [
        { key: "3a", href: "#3a", title: "3a. Property You Own" },
        { key: "3b", href: "#3b", title: "3b. Additional Property" },
        { key: "3c", href: "#3c", title: "3c. Additional Property" },
    ]},
    { key: "s4", href: "#s4", title: "S4. Loan and Property Information", children: [
        { key: "4a", href: "#4a", title: "4a. Loan and Property Information" },
        { key: "4b", href: "#4b", title: "4b. Other New Mortgage Loans on the Property" },
        { key: "4c", href: "#4c", title: "4c. Rental Income on the Property" },
        { key: "4d", href: "#4d", title: "4d. Gifts or Grants" },
    ]},
    { key: "s5", href: "#s5", title: "S5. Declarations.", children: [
        { key: "5a", href: "#5a", title: "5a. About this Property and Your Money for this Loan" },
        { key: "5b", href: "#5b", title: "5b. About Your Finances" },
    ]},
    { key: "s6", href: "#s6", title: "S6. Acknowledgments and Agreements" },
    { key: "s7", href: "#s7", title: "S7. Military Service" },
    { key: "s8", href: "#s8", title: "S8. Demographic Information" },
    { key: "s9", href: "#s9", title: "S9. Loan Originator Information (to be completed by the loan originator)" },
]

export const isLenderLoanInformationEnabled = () => {
    try {
        const raw = localStorage.getItem("configurations")
        if (!raw) return false
        return !!JSON.parse(raw)?.enableLenderLoanInformation
    } catch (_err) {
        return false
    }
}

/**
 * @param {boolean} includeLenderLoanForm
 * @param {{ compact?: boolean }} [options] compact=true → short labels (VerifyModal); false → full titles (Borrower 1003)
 */
export const buildV2AnchorItems = (includeLenderLoanForm = isLenderLoanInformationEnabled(), options = {}) => {
    const compact = options.compact !== false
    const list = [...(compact ? v2AnchorItemsCompact : v2AnchorItemsFull)]
    if (includeLenderLoanForm) {
        list.push(compact ? {
            key: "LenderLoanForm",
            href: "#LenderLoanForm",
            title: "LL",
            children: [
                { key: "L1", href: "#L1", title: "L1" },
                { key: "L2", href: "#L2", title: "L2" },
                { key: "L3", href: "#L3", title: "L3" },
                { key: "L4", href: "#L4", title: "L4" },
            ],
        } : {
            key: "LenderLoanForm",
            href: "#LenderLoanForm",
            title: "Lender Loan Information",
            children: [
                { key: "L1", href: "#L1", title: "L1. Property & Loan Info" },
                { key: "L2", href: "#L2", title: "L2. Title Info" },
                { key: "L3", href: "#L3", title: "L3. Mortgage Loan Info" },
                { key: "L4", href: "#L4", title: "L4. Qualifying the Borrower" },
            ],
        })
    }
    return list
}

export const v2AnchorItems = buildV2AnchorItems()

const creditLoanTypeOptions = [
    { value: "ConventionalRealEstateMortgage", label: "Conventional" },
    { value: "FHARealEstateMortgage", label: "FHA" },
    { value: "VeteransAdministrationRealEstateMortgage", label: "VA" },
    { value: "USDA-RD", label: "USDA-RD" },
    { value: "Other", label: "Other" },
]

const ownedPropertyTypeOptions = [
    { value: "singlefamily", label: "Single family" },
    { value: "townhouse", label: "Townhouse" },
    { value: "twotofour", label: "Multi family" },
    { value: "condominium", label: "Condo" },
    { value: "mobilehome", label: "Manufactured home" },
]

// The borrower app's cards store Single family as singlefamilydetached, PUD as
// puddetached and Multifamily (5+ unit) as commercial, and MISMO import writes
// singlefamilyattached for an attached home; every one of them needs a label
// here or the field shows the raw value.
const subjectPropertyTypeOptions = [
    { value: "singlefamilydetached", label: "Single family" },
    { value: "singlefamilyattached", label: "Single family (attached)" },
    { value: "puddetached", label: "PUD" },
    { value: "pudattached", label: "PUD (attached)" },
    { value: "twotofourfamily", label: "Multifamily (1-4 unit)" },
    { value: "commercial", label: "Multifamily (5+ unit)" },
    { value: "condominium", label: "Condo" },
    { value: "mobilehome", label: "Manufactured home" },
]

const emptyPropertyMortgage = () => ({
    key: `pm-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    creditLoanType: "ConventionalRealEstateMortgage",
    name: "",
    accountNumber: "",
    monthlyPayment: "",
    remainingBalance: "",
    paidOffAtClosing: "",
    creditLimit: "",
})

// Full freeform line for parkAddress (and non-subject REO address companion).
// Subject property.address and street-semantic fields stay street-only per URLA Street box.
export const fullAddressFromPlace = (parsed) => {
    const street = parsed.street || ""
    const city = parsed.city || ""
    const st = parsed.stateAbbr || parsed.state || ""
    const zip = parsed.zip || ""
    const cityLine = [city, [st, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")
    return [street, cityLine].filter(Boolean).join(", ")
}

const numOrZero = (value) => parseFloat(value) || 0

const normalizePropertyLoan = (loan) => ({
    ...loan,
    creditLoanType: loan.creditLoanType || loan.creditloantype || "ConventionalRealEstateMortgage",
    name: loan.name || "",
    accountNumber: loan.accountNumber || loan.accountnumber || "",
    monthlyPayment: loan.monthlyPayment || loan.monthlypayment || "",
    remainingBalance: loan.remainingBalance || loan.remainingbalance || "",
    paidOffAtClosing: loan.paidOffAtClosing || loan.paidoffatclosing || "",
    creditLimit: loan.creditLimit || loan.creditlimit || "",
})

// Escrow answer on a Section 3 mortgage: true / false / unanswered (stored as absent).
const escrowValue = (loan) => (loan.paymentIncludesTaxesInsurance === true ? "yes" : loan.paymentIncludesTaxesInsurance === false ? "no" : "")
const escrowOptions = [
    { value: "", label: "Not sure" },
    { value: "yes", label: "Yes (escrowed)" },
    { value: "no", label: "No" },
]

const PropertyMortgageList = ({ loans, onChange, readOnly = false, askEscrow = false }) => {
    const list = (Array.isArray(loans) ? loans : []).map(normalizePropertyLoan)
    const setLoan = (index, partial) => {
        if (readOnly) return
        const next = [...list]
        next[index] = { ...(next[index] || {}), ...partial }
        onChange(next)
    }
    return (
        <div>
            {list.map((loan, i) => (
                <div className="removable p-3 mb-3" key={loan.key || i}>
                    <div className="text-right">
                        {readOnly ? null : <DeleteControl onConfirm={() => onChange(list.filter((_, idx) => idx !== i))} />}
                    </div>
                    <Row>
                        <Col sm={4} className="mb-3">
                            <Field label="Type">
                                <ChromeSelect
                                    style={{ width: "100%" }}
                                    value={loan.creditLoanType || "ConventionalRealEstateMortgage"}
                                    options={withCurrentOption(creditLoanTypeOptions, loan.creditLoanType)}
                                    onChange={(v) => setLoan(i, { creditLoanType: v })}
                                />
                            </Field>
                        </Col>
                        <Col sm={4} className="mb-3"><TextField label="Creditor name" value={loan.name} onChange={(v) => setLoan(i, { name: v })} /></Col>
                        <Col sm={4} className="mb-3"><TextField label="Account Number" value={loan.accountNumber} onChange={(v) => setLoan(i, { accountNumber: v })} /></Col>
                    </Row>
                    <Row>
                        <Col sm={4} className="mb-3"><SectionAmountField label="Mo. Payment" prefix="$" suffix="mo" value={loan.monthlyPayment} normalizeOnBlur="dollar" onChange={(v) => setLoan(i, { monthlyPayment: v })} /></Col>
                        <Col sm={4} className="mb-3"><SectionAmountField label="Unpaid Balance" prefix="$" value={loan.remainingBalance} normalizeOnBlur="dollar" onChange={(v) => setLoan(i, { remainingBalance: v })} /></Col>
                        <Col sm={4} className="mb-3">
                            <Field label="Paid off at/before closing">
                                <ChromeSelect
                                    style={{ width: "100%" }}
                                    value={loan.paidOffAtClosing || ""}
                                    options={[{ value: "", label: "Select" }, ...paidOffOptions]}
                                    onChange={(v) => setLoan(i, { paidOffAtClosing: v })}
                                />
                            </Field>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={4} className="mb-3"><SectionAmountField label="Credit limit (if applicable)" prefix="$" value={loan.creditLimit} normalizeOnBlur="dollar" onChange={(v) => setLoan(i, { creditLimit: v })} /></Col>
                        {askEscrow && (
                            <Col sm={4} className="mb-3">
                                {/* Credit reports do not say; the DTI calculator asks until this is answered. */}
                                <Field label="Payment includes taxes & insurance?">
                                    <ChromeSelect
                                        style={{ width: "100%" }}
                                        value={escrowValue(loan)}
                                        options={escrowOptions}
                                        onChange={(v) => setLoan(i, { paymentIncludesTaxesInsurance: v === "yes" ? true : v === "no" ? false : undefined })}
                                    />
                                </Field>
                            </Col>
                        )}
                    </Row>
                </div>
            ))}
            {list.length < 2 && !readOnly && (
                <AddLink onClick={() => onChange([...list, emptyPropertyMortgage()])}>Add mortgage loans on this property</AddLink>
            )}
        </div>
    )
}

const ReoCard = ({
    item,
    index,
    isSubject,
    extraLabel,
    onField,
    onFields,
    onLoans,
    onRemove,
    loanLimits,
    readOnly = false,
    ownership,
}) => {
    const occupancyOptions = isSubject
        ? [
            { value: "principal", label: "Primary Residence" },
            { value: "secondhome", label: "Second Home" },
            { value: "investment", label: "Investment" },
            { value: "fhasecond", label: "FHA Secondary Residence" },
        ]
        : [
            { value: "principal", label: "Primary Residence" },
            { value: "secondhome", label: "Second Home" },
            { value: "investment", label: "Investment" },
            { value: "other", label: "Other" },
        ]
    const typeOptions = isSubject ? subjectPropertyTypeOptions : ownedPropertyTypeOptions
    const propertyType = isSubject ? (item.propertytype || item.propertyType) : (item.propertyType || item.propertytype)
    const occupancy = item.occupancy || ""
    const zip = isSubject ? (item.zipcode || item.zip) : (item.zip || item.zipcode)
    const propertyValue = isSubject ? item.appraisal : item.marketValue
    const totalKey = isSubject ? "insurancemaintenancetaxes" : "insuranceMaintenanceTaxes"
    const rentalKey = isSubject ? "grossrentalincome" : "grossRentalIncome"
    const netRentalKey = isSubject ? "netrentalincome" : "netRentalIncome"
    const typeKey = isSubject ? "propertytype" : "propertyType"
    const showRental = rentalIncomeApplies(occupancy, propertyType)

    // withRentalRule adds the rental income fields to a change when that change
    // hides them.
    //
    // Both controls used to blank the amount on every change, whichever way it
    // went - so picking Investment threw away a figure just typed - and neither
    // touched the net amount, which stayed behind on a property that could no
    // longer earn anything.
    const withRentalRule = (next) => {
        const nextOccupancy = next.occupancy !== undefined ? next.occupancy : occupancy
        const nextType = next[typeKey] !== undefined ? next[typeKey] : propertyType
        if (rentalIncomeApplies(nextOccupancy, nextType)) return next
        return { ...next, [rentalKey]: "", [netRentalKey]: "" }
    }
    const insurance = numOrZero(item.insurancemain)
    const tax = numOrZero(item.insurancetax)
    const storedTotal = numOrZero(item[totalKey])
    const pmi = isSubject ? numOrZero(item.insurancepmi) : 0
    const hoa = item.insurancehoa !== undefined && item.insurancehoa !== ""
        ? numOrZero(item.insurancehoa)
        : Math.max(0, storedTotal - insurance - tax - pmi)

    const setInsuranceParts = (next) => {
        const main = next.insurancemain !== undefined ? numOrZero(next.insurancemain) : insurance
        const nextTax = next.insurancetax !== undefined ? numOrZero(next.insurancetax) : tax
        const nextHoa = next.insurancehoa !== undefined ? numOrZero(next.insurancehoa) : hoa
        const total = String(main + nextTax + nextHoa + pmi)
        if (readOnly) return
        onFields({
            insurancemain: next.insurancemain !== undefined ? next.insurancemain : (item.insurancemain || ""),
            insurancetax: next.insurancetax !== undefined ? next.insurancetax : (item.insurancetax || ""),
            insurancehoa: next.insurancehoa !== undefined ? next.insurancehoa : String(hoa || ""),
            [totalKey]: total,
        })
    }

    const field = (key, value) => {
        if (readOnly) return
        onField(key, value)
    }
    const fields = (next) => {
        if (readOnly) return
        onFields(next)
    }
    const loansChange = (nextLoans) => {
        if (readOnly) return
        onLoans(nextLoans)
    }

    return (
        <div className="removable p-3 mb-3" style={readOnly ? { pointerEvents: "none", opacity: 0.92 } : undefined}>
            {(extraLabel || onRemove || readOnly) ? (
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="fontWeight500">
                        {extraLabel || ""}
                        {readOnly ? (
                            <span className="ml-2" style={{ color: "#325CEB", fontWeight: 500 }}>Shared with you</span>
                        ) : null}
                    </div>
                    {onRemove && !readOnly ? <DeleteControl confirm={false} onConfirm={onRemove} /> : null}
                </div>
            ) : null}
            <StructuredAddressField
                id={`reo-address-${isSubject ? "subject" : index}`}
                label="Address"
                address={{
                    street: item.street || item.address,
                    unit: item.unit,
                    state: item.state,
                    county: item.county,
                    city: item.city,
                    zip,
                }}
                onChange={(key, value) => {
                    if (key === "street") return fields({ address: value, street: value })
                    if (key === "zip") return fields(isSubject ? { zipcode: value, zip: value } : { zip: value, zipcode: value })
                    field(key, value)
                }}
                onPlace={(parsed) => fields({
                    // Subject property.address is the URLA Street box — street-only.
                    // Other REO keep address as freeform companion; street stays street-only for DB.
                    address: isSubject ? (parsed.street || "") : fullAddressFromPlace(parsed),
                    street: parsed.street,
                    city: parsed.city,
                    state: parsed.state,
                    county: parsed.county,
                    zip: parsed.zip,
                    zipcode: parsed.zip,
                })}
                loanLimits={loanLimits}
            />
            {ownership?.show ? (
                <Row>
                    <OwnershipFields
                        layout="reo"
                        currentPartyId={ownership.currentPartyId}
                        ownership={item.ownership}
                        ownerPartyIds={item.ownerPartyIds}
                        otherParticipants={ownership.otherParticipants}
                        disabled={!ownership.canEdit || readOnly}
                        onChange={(patch) => {
                            if (readOnly || !ownership.canEdit) return
                            ownership.onChange(patch)
                        }}
                    />
                </Row>
            ) : null}
            <Row>
                <Col sm={6} className="mb-4">
                    <SectionAmountField
                        label="Property value"
                        prefix="$"
                        value={propertyValue}
                        normalizeOnBlur="dollar"
                        onChange={(v) => field(isSubject ? "appraisal" : "marketValue", v)}
                    />
                </Col>
                <Col sm={6} className="mb-4">
                    <Field label="Status">
                        <ChromeSelect
                            style={{ width: "100%" }}
                            value={item.status || ""}
                            options={[
                                { value: "", label: "Select status" },
                                { value: "sold", label: "Sold" },
                                { value: "pending", label: "Pending Sale" },
                                { value: "retained", label: "Retained" },
                            ]}
                            onChange={(v) => field("status", v)}
                        />
                    </Field>
                </Col>
            </Row>
            <Row>
                <Col sm={6} className="mb-4">
                    <Field label="Intended occupancy">
                        <ChromeSelect
                            style={{ width: "100%" }}
                            value={occupancy}
                            options={occupancyOptions}
                            onChange={(v) => fields(withRentalRule({ occupancy: v }))}
                        />
                    </Field>
                </Col>
                <Col sm={6} className="mb-4">
                    <Field label="Property type">
                        <ChromeSelect
                            style={{ width: "100%" }}
                            value={propertyType || ""}
                            options={[{ value: "", label: "Select type" }, ...withCurrentOption(typeOptions, propertyType)]}
                            onChange={(v) => fields(withRentalRule({ [typeKey]: v }))}
                        />
                    </Field>
                </Col>
            </Row>
            <div className="mb-4 insurance-and-tax-breakdown">
                {storedTotal > 0 && Math.abs(storedTotal - (insurance + tax + hoa + pmi)) > 0.01 && (
                    <Row>
                        <Col>
                            <Alert
                                className="insurance-and-tax-breakdown-alert"
                                type="warning"
                                showIcon
                                closable
                                onClose={() => {
                                    if (insurance + tax + hoa + pmi === 0 && storedTotal > 0) {
                                        setInsuranceParts({ insurancemain: String(storedTotal) })
                                    }
                                }}
                                message={`Amounts are now entered in separate fields for Insurance, Tax, and HOA fees. Previous total ($${storedTotal.toLocaleString()}) moved to Insurance. Please review and update each amount to match actual costs.`}
                            />
                        </Col>
                    </Row>
                )}
                <Row className="mb-4">
                    <Col>
                        <SectionAmountField
                            label={isSubject
                                ? "Monthly insurance, taxes, association dues, etc."
                                : "Monthly insurance, taxes, association dues, etc. (if not included in the mortgage payment)"}
                            prefix="$"
                            value={item[totalKey]}
                            normalizeOnBlur="dollar"
                            onChange={(v) => field(totalKey, v)}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col sm={4} className="mb-4">
                        <div className="insurance-and-tax-breakdown-label">Insurance (monthly)</div>
                        <Space.Compact style={{ width: "100%" }}>
                            <InputNumber
                                prefix="$"
                                className="estimated-monthly-payment-input"
                                style={{ flex: 1 }}
                                controls={false}
                                stringMode
                                value={item.insurancemain || "0"}
                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                parser={(v) => moneyTypingValue(v, 2)}
                                onChange={(v) => setInsuranceParts({ insurancemain: v == null || v === "" ? "" : moneyTypingValue(v, 2) })}
                            />
                            <InputNumber
                                suffix="%"
                                className="estimated-monthly-payment-input"
                                style={{ width: 74 }}
                                min={0}
                                max={100}
                                step={0.01}
                                controls={false}
                                value={(() => {
                                    const pv = numOrZero(propertyValue) || 300000
                                    return pv > 0 ? Math.round((insurance / pv) * 10000) / 100 : 0
                                })()}
                                onChange={(v) => {
                                    if (v === null || v === undefined) return
                                    const pv = numOrZero(propertyValue) || 300000
                                    setInsuranceParts({ insurancemain: dollarValue(String((v / 100) * pv)) })
                                }}
                            />
                        </Space.Compact>
                    </Col>
                    <Col sm={4} className="mb-4">
                        <div className="insurance-and-tax-breakdown-label">Tax (monthly)</div>
                        <Space.Compact style={{ width: "100%" }}>
                            <InputNumber
                                prefix="$"
                                className="estimated-monthly-payment-input"
                                style={{ flex: 1 }}
                                controls={false}
                                stringMode
                                value={item.insurancetax || "0"}
                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                parser={(v) => moneyTypingValue(v, 2)}
                                onChange={(v) => setInsuranceParts({ insurancetax: v == null || v === "" ? "" : moneyTypingValue(v, 2) })}
                            />
                            <InputNumber
                                suffix="%"
                                className="estimated-monthly-payment-input"
                                style={{ width: 74 }}
                                min={0}
                                max={100}
                                step={0.01}
                                controls={false}
                                value={(() => {
                                    const pv = numOrZero(propertyValue) || 300000
                                    return pv > 0 ? Math.round((tax / pv) * 10000) / 100 : 0
                                })()}
                                onChange={(v) => {
                                    if (v === null || v === undefined) return
                                    const pv = numOrZero(propertyValue) || 300000
                                    setInsuranceParts({ insurancetax: dollarValue(String((v / 100) * pv)) })
                                }}
                            />
                        </Space.Compact>
                    </Col>
                    <Col sm={4} className="mb-4">
                        <div className="insurance-and-tax-breakdown-label">HOA fee (monthly)</div>
                        <InputNumber
                            prefix="$"
                            className="estimated-monthly-payment-input"
                            style={{ width: "100%" }}
                            controls={false}
                            stringMode
                            value={item.insurancehoa !== undefined && item.insurancehoa !== "" ? item.insurancehoa : String(hoa || 0)}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            parser={(v) => moneyTypingValue(v, 2)}
                            onChange={(v) => setInsuranceParts({ insurancehoa: v == null || v === "" ? "" : moneyTypingValue(v, 2) })}
                        />
                    </Col>
                </Row>
            </div>
            {showRental && (
                <Row className="mb-4">
                    <Col md={6}>
                        <SectionAmountField
                            label="Expected monthly rental income"
                            prefix="$"
                            value={item[rentalKey] || item.grossRentalIncome || item.grossrentalincome}
                            normalizeOnBlur="dollar"
                            onChange={(v) => field(rentalKey, v)}
                        />
                    </Col>
                </Row>
            )}
            <PropertyMortgageList loans={item.loans} onChange={loansChange} readOnly={readOnly} askEscrow={!isSubject} />
        </div>
    )
}

export default function Interview1003ViewV2More({
    participant,
    application,
    showNonOwnSharedOwnerWarning = false,
    onDismissNonOwnSharedOwnerWarning,
    onOpenCreditImport,
    hasAvailableCreditReport = false,
    creditReportCreatedAt = "",
    onOpenIncomeCalculation,
    onEmploymentLists,
    onEmploymentItem,
    onOtherIncome,
    onOtherIncomeItem,
    onAssets,
    onAssetItem,
    onLiabilities,
    onLiabilityItem,
    onNewMortgages,
    onRealEstate,
    onSubjectLoans,
    onProperty,
    onPropertyFields,
    onSubjectMerge,
    onSelection,
    onLoanBalance,
    onDeclaration,
    onBrokerInfo,
    onBrokerPlace,
    loanLimits,
}) {
    const { editMode } = useFormChrome()
    const [showOwnPropertyWarning1, setShowOwnPropertyWarning1] = useState(false)
    const [showOwnPropertyWarning2, setShowOwnPropertyWarning2] = useState(false)
    const otherJobs = participant.otheroccupations || []
    const prevJobs = participant.previousoccupations || []
    const otherIncome = participant.income?.otherincome || []
    const assets = application?.finance?.assetsByPartyId?.[participant.partyId] || { accounts: [], otherAssets: [], gifts: [] }
    const accounts = assets.accounts || []
    const otherAssets = assets.otherAssets || []
    const liabilities = application?.finance?.liabilitiesByPartyId?.[participant.partyId] || { loans: [], additionalExpenses: [] }
    const loans = liabilities.loans || []
    const expenses = (liabilities.additionalExpenses && liabilities.additionalExpenses.length)
        ? liabilities.additionalExpenses
        : (participant.income?.alimony || [])
    const newMortgages = (application?.finance?.liabilitiesByPartyId?.[application?.primaryPartyId] || liabilities).newMortgages || []
    const gifts = assets.gifts || []
    const property = application?.property || {}
    // URLA 4c rental fields: only for purchase + (investment OR primary 2-4).
    // Clear stale amounts when hidden so PDF / MISMO do not export leftover income.
    const showsPurchaseRentalIncome = (p) => (
        p?.purpose === "purchase" && (
            p?.occupancy === "investment" ||
            (p?.propertytype === "twotofourfamily" && p?.occupancy === "principal")
        )
    )
    const clearPurchaseRentalIfHidden = (patch) => {
        const next = { ...property, ...patch }
        if (showsPurchaseRentalIncome(next)) return
        const purchase = property.purchase || {}
        if (!purchase.expectedgrossmonthlyrentalincome && !purchase.expectednetmonthlyrentalincome) return
        if (onSubjectMerge) {
            onSubjectMerge({
                purchase: {
                    expectedgrossmonthlyrentalincome: "",
                    expectednetmonthlyrentalincome: "",
                },
            })
        }
    }
    // Section 3 / subject REO rental — same rule as ReoCard showRental / legacy.
    // 4a shares subject property on refinance; clear when the 3a field would hide.
    const showsSubjectRentalIncome = (p) => (
        p?.occupancy === "investment" ||
        p?.propertytype === "twotofour"
    )
    const isClearedSubjectRental = (v) => v === undefined || v === null || v === "" || v === "0"
    const clearSubjectRentalIfHidden = (patch) => {
        const next = { ...property, ...patch }
        if (showsSubjectRentalIncome(next)) return
        if (isClearedSubjectRental(property.grossrentalincome) && isClearedSubjectRental(property.netrentalincome)) return
        onPropertyFields({ grossrentalincome: "0", netrentalincome: "0" })
    }
    const isPurchase = property.purpose === "purchase"
    const storedReo = application?.finance?.realEstateByPartyId?.[participant.partyId]?.properties || []
    const reo = assemblePartyOwnedProperties({
        stored: storedReo,
        partyId: participant.partyId,
        present: participant.presentAddress,
        ownership: participant.residenceOwnership,
        subject: property,
        isPurchaseLoan: isPurchase,
    })
    const patchOwned = (index, partial) => {
        const next = reo.map((item, i) => (i === index ? { ...item, ...partial } : item))
        onRealEstate({ properties: next })
    }
    const selection = application?.selection || {}
    const subjectValue = property.purpose === "purchase" ? property.salesprice : property.appraisal
    const needsPMI = numOrZero(subjectValue) > 0 && numOrZero(selection.loansize) / numOrZero(subjectValue) > 0.8
    const mobile = property.mobilehomeinfo || {}
    const syncLoanRelated = ({ salesprice, appraisal, loanbalance, loansize, skipRelated } = {}) => {
        const purpose = property.purpose
        const price = numOrZero(
            salesprice !== undefined ? salesprice : (purpose === "purchase" ? property.salesprice : (appraisal !== undefined ? appraisal : property.appraisal))
        )
        const size = numOrZero(loansize !== undefined ? loansize : selection.loansize)
        const balance = numOrZero(loanbalance !== undefined ? loanbalance : application?.loanbalance)
        if (!skipRelated) {
            if (purpose === "purchase") {
                onSelection("downpayment", price >= size ? String(price - size) : "0")
            } else if (purpose === "cashoutrefinance") {
                if (price >= size && size >= balance) onSelection("cashout", String(size - balance))
            } else if (purpose === "refinance") {
                // Always write paydown. An invalid loan (balance < amount) used to skip the
                // update and leave a stale value that Rate recommendation still displayed.
                onSelection("paydown", balance >= size ? String(balance - size) : "0")
            }
        }
        const ltv = price > 0 ? size / price : 0
        if (ltv <= 0.8 && numOrZero(property.insurancepmi) > 0) {
            const total = numOrZero(property.insurancemain) + numOrZero(property.insurancetax) + numOrZero(property.insurancehoa)
            onPropertyFields({ insurancepmi: "0", insurancemaintenancetaxes: String(total) })
        }
    }
    const setSubjectInsurance = (partial) => {
        const main = partial.insurancemain !== undefined ? numOrZero(partial.insurancemain) : numOrZero(property.insurancemain)
        const tax = partial.insurancetax !== undefined ? numOrZero(partial.insurancetax) : numOrZero(property.insurancetax)
        const hoa = partial.insurancehoa !== undefined ? numOrZero(partial.insurancehoa) : numOrZero(property.insurancehoa)
        const pmi = partial.insurancepmi !== undefined ? numOrZero(partial.insurancepmi) : numOrZero(property.insurancepmi)
        onPropertyFields({
            insurancemain: partial.insurancemain !== undefined ? partial.insurancemain : (property.insurancemain || ""),
            insurancetax: partial.insurancetax !== undefined ? partial.insurancetax : (property.insurancetax || ""),
            insurancehoa: partial.insurancehoa !== undefined ? partial.insurancehoa : (property.insurancehoa || ""),
            insurancepmi: partial.insurancepmi !== undefined ? partial.insurancepmi : (property.insurancepmi || ""),
            insurancemaintenancetaxes: String(main + tax + hoa + pmi),
        })
    }
    const loanAmountError = (() => {
        const size = numOrZero(selection.loansize)
        const balance = numOrZero(application?.loanbalance)
        if (!size) return ""
        if (property.purpose === "purchase" && numOrZero(property.salesprice) > 0 && numOrZero(property.salesprice) < size) {
            return "Loan amount cannot be greater than the property value. Please enter a suitable loan amount!"
        }
        if (property.purpose === "cashoutrefinance" && balance > 0 && size < balance) {
            return "Loan amount cannot be less than the current loan balance. Please enter a suitable loan amount!"
        }
        if (property.purpose === "refinance" && balance < size) {
            return "Loan amount cannot be more than the current loan balance. Please enter a suitable loan amount!"
        }
        return ""
    })()
    const validateLoanAmount = (raw) => {
        const size = numOrZero(raw)
        if (property.purpose === "purchase" && numOrZero(property.salesprice) < size) {
            message.warning("Loan amount cannot be greater than the property value. Please enter a suitable loan amount!")
        } else if (property.purpose === "cashoutrefinance" && size < numOrZero(application?.loanbalance)) {
            message.warning("Loan amount cannot be less than the current loan balance. Please enter a suitable loan amount!")
        } else if (property.purpose === "refinance" && numOrZero(application?.loanbalance) < size) {
            message.warning("Loan amount cannot be more than the current loan balance. Please enter a suitable loan amount!")
        }
    }
    const declarationsRaw = application?.declarationsByPartyId?.[participant.partyId] || {}
    // Match legacy State.js / backend defaults for 5a follow-up dropdowns.
    const declarations = {
        propertyowned: "principal",
        titleheld: "yourself",
        ...declarationsRaw,
    }
    const military = declarations.militaryservice || {}
    const eo = { ...emptyEO(), ...(declarations.equalopportunity || {}) }
    const eoHispanic = { ...emptyEO().hispanic, ...(eo.hispanic || {}) }
    const eoRace = { ...emptyEO().race, ...(eo.race || {}) }

    const currentPartyId = participant?.partyId || ""
    const otherParticipants = Object.values(application?.participantsById || {})
        .filter((person) => {
            if (!person?.partyId || person.partyId === currentPartyId) return false
            const role = `${person.role || ""}`.toLowerCase()
            return person.isBorrower || person.isPrimary || role === "borrower" || role === "coborrower"
        })
    const showOwnership = otherParticipants.length > 0
    const ownershipProps = (item, onChange) => ({
        currentPartyId,
        ownership: item.ownership,
        ownerPartyIds: item.ownerPartyIds,
        otherParticipants,
        onChange,
    })
    const primaryPartyId = application?.primaryPartyId || ""
    const subjectOwnerIds = Array.isArray(property.ownerPartyIds)
        ? property.ownerPartyIds.filter(Boolean)
        : []
    const subjectHomePartyId = property.homePartyId || primaryPartyId
    const canEditSubject = Boolean(currentPartyId) && currentPartyId === subjectHomePartyId
    const showSubject = !isPurchase && (
        subjectOwnerIds.length > 0
            ? subjectOwnerIds.includes(currentPartyId)
            : currentPartyId === primaryPartyId
    )
    const subjectIn3a = showSubject
    const mapOwnedAsPurchase = isPurchase || !subjectIn3a
    const sharedFromOthers = (application?.participantOrder || []).flatMap((id) => {
        if (!id || id === currentPartyId) return []
        // Align with Content RealEstateV2: address-dedupe Shared-with-you only when Own.
        // Otherwise matching present address must not hide the shared REO.
        const isOwn = `${participant.residenceOwnership || ""}`.toLowerCase() === "own"
        const street = `${participant.presentAddress?.street || participant.presentAddress?.address || ""}`.trim()
        const presentKey = isOwn && street ? ownedLocationKey(participant.presentAddress) : ""
        return (application?.finance?.realEstateByPartyId?.[id]?.properties || [])
            .filter((item) => (item.ownerPartyIds || []).includes(currentPartyId))
            .filter((item) => !isPartyCurrentResidenceRow(item, currentPartyId, presentKey))
            .map((item) => ({ ...item, homePartyId: item.homePartyId || id }))
    })
    const writeSubjectField = (key, value) => {
        if (!canEditSubject) return
        onProperty(key, value)
    }
    const writeSubjectFields = (next) => {
        if (!canEditSubject) return
        onPropertyFields(next)
    }
    const writeSubjectLoans = (nextLoans) => {
        if (!canEditSubject) return
        onSubjectLoans(nextLoans)
    }
    const reoOwnership = (item, onChange) => (
        showOwnership
            ? {
                show: true,
                currentPartyId,
                otherParticipants,
                canEdit: true,
                onChange,
            }
            : null
    )
    const firstOwned = reo[0]
    const extraIndex = mapOwnedAsPurchase ? 1 : 0
    const extraOwnedItem = reo[extraIndex]
    const doesNotApply3b = mapOwnedAsPurchase ? reo.length < 2 : reo.length < 1
    const thirdIndex = mapOwnedAsPurchase ? 2 : 1
    const thirdOwnedItem = reo[thirdIndex]
    const doesNotApply3c = mapOwnedAsPurchase ? reo.length < 3 : reo.length < 2
    const moreOwned = mapOwnedAsPurchase ? reo.slice(3) : reo.slice(2)
    const padOwnedTo = (n) => {
        const next = [...reo]
        while (next.length < n) next.push(emptyOwned(currentPartyId))
        return next
    }
    const linkedPropertyId = (item) => {
        if (item.forSubjectProperty) return "subject"
        if ((property.loans || []).some((loan) => loanKeyMatches(loan.key, item.key))) return "subject"
        const reoIndex = reo.findIndex((owned) => (owned.loans || []).some((loan) => loanKeyMatches(loan.key, item.key)))
        if (reoIndex >= 0) return `reo-${reoIndex}`
        const subjectAddress = `${property.address || ""}`.trim()
        if (subjectIn3a && canEditSubject && subjectAddress && item.propertyAddress === subjectAddress) return "subject"
        const byAddress = reo.findIndex((owned) => propertyAddressText(owned) && owned && item.propertyAddress && propertyAddressText(owned) === item.propertyAddress)
        return byAddress >= 0 ? `reo-${byAddress}` : ""
    }
    const stripLoanFromReo = (properties, loanKey) => properties.map((owned) => ({
        ...owned,
        loans: stripLoanFromList(owned.loans, loanKey),
    }))
    const unlinkLiabilityFromProperties = (loanIndex) => {
        const item = loans[loanIndex]
        if (!item) return
        onRealEstate({ properties: stripLoanFromReo(reo, item.key) })
        writeSubjectLoans(stripLoanFromList(property.loans, item.key))
        onLiabilityItem("loans", loanIndex, { propertyAddress: "", forSubjectProperty: false })
    }
    // Keep Section 3 mortgage copies in sync when 2c Lease fields change (legacy shared-key semantics).
    const syncLinkedMortgageFromLiability = (item) => {
        if (!item?.key) return
        if (!(item.creditLoanType === "Mortgage" || item.forSubjectProperty || item.propertyAddress)) return
        const syncList = (list, address) => (list || []).map((loan) => (
            loanKeyMatches(loan.key, item.key)
                ? {
                    ...loan,
                    ...loanCopyForProperty(item, address || item.propertyAddress || loan.propertyAddress || ""),
                    creditLoanType: loan.creditLoanType || "ConventionalRealEstateMortgage",
                }
                : loan
        ))
        const nextSubject = syncList(property.loans, property.address)
        const nextReo = reo.map((owned) => ({
            ...owned,
            loans: syncList(owned.loans, propertyAddressText(owned)),
        }))
        const subjectChanged = JSON.stringify(nextSubject) !== JSON.stringify(property.loans || [])
        const reoChanged = JSON.stringify(nextReo) !== JSON.stringify(reo)
        if (subjectChanged) writeSubjectLoans(nextSubject)
        if (reoChanged) onRealEstate({ properties: nextReo })
    }
    const onLinkedLiabilityItem = (listKey, index, partial) => {
        onLiabilityItem(listKey, index, partial)
        if (listKey !== "loans") return
        const next = { ...(loans[index] || {}), ...partial }
        syncLinkedMortgageFromLiability(next)
    }
    // When Section 3 mortgage rows change, mirror shared fields back to the 2c Lease row.
    const onPropertyLoansChange = (nextLoans, { isSubject = false, reoIndex = -1 } = {}) => {
        if (isSubject) writeSubjectLoans(nextLoans)
        else if (reoIndex >= 0) patchOwned(reoIndex, { loans: nextLoans, ownedFreeAndClear: !nextLoans.length })
        const twoC = loans.map((row) => ({ ...row }))
        let changed = false
        nextLoans.forEach((loan) => {
            const idx = twoC.findIndex((row) => loanKeyMatches(row.key, loan.key) && row.creditLoanType === "Mortgage")
            if (idx < 0) return
            twoC[idx] = {
                ...twoC[idx],
                name: loan.name ?? twoC[idx].name,
                accountNumber: loan.accountNumber ?? twoC[idx].accountNumber,
                monthlyPayment: loan.monthlyPayment ?? twoC[idx].monthlyPayment,
                remainingBalance: loan.remainingBalance ?? twoC[idx].remainingBalance,
                paidOffAtClosing: loan.paidOffAtClosing ?? twoC[idx].paidOffAtClosing,
                ownership: loan.ownership ?? twoC[idx].ownership,
                ownerPartyIds: loan.ownerPartyIds ?? twoC[idx].ownerPartyIds,
            }
            changed = true
        })
        twoC.forEach((row, idx) => {
            if (row.creditLoanType !== "Mortgage") return
            const linkedId = linkedPropertyId(row)
            const linkedHere = isSubject ? linkedId === "subject" : linkedId === `reo-${reoIndex}`
            if (!linkedHere) return
            const stillLinked = nextLoans.some((loan) => loanKeyMatches(loan.key, row.key))
            if (stillLinked) return
            twoC[idx] = { ...row, propertyAddress: "", forSubjectProperty: false }
            changed = true
        })
        if (changed) onLiabilities({ loans: twoC })
    }
    const onLiabilityTypeChange = (index, value) => {
        if (value !== "Mortgage" && (loans[index]?.creditLoanType === "Mortgage" || linkedPropertyId(loans[index]))) {
            unlinkLiabilityFromProperties(index)
        }
        // creditLoanType is what the Category control binds to; also write
        // creditLiabilityAccountType so PDF/MISMO (which read that metadata key)
        // get Account Type without relying on a server-side fallback alone.
        onLinkedLiabilityItem("loans", index, { creditLoanType: value, creditLiabilityAccountType: value })
    }
    const removeLiability = (index) => {
        const item = loans[index]
        if (item?.key && (item.creditLoanType === "Mortgage" || item.propertyAddress || item.forSubjectProperty)) {
            onRealEstate({ properties: stripLoanFromReo(reo, item.key) })
            writeSubjectLoans(stripLoanFromList(property.loans, item.key))
        }
        onLiabilities({ loans: loans.filter((_, idx) => idx !== index) })
    }

    const setReo = (index, key, value) => {
        const next = [...reo]
        next[index] = { ...next[index], [key]: value }
        if (key === "address") next[index].street = value
        if (key === "zip") next[index].zipcode = value
        onRealEstate({ properties: next })
    }
    const setReoPlace = (index, parsed) => {
        const next = [...reo]
        next[index] = {
            ...next[index],
            address: parsed.street,
            street: parsed.street,
            city: parsed.city,
            state: parsed.state,
            zip: parsed.zip,
            zipcode: parsed.zip,
            county: parsed.county,
        }
        onRealEstate({ properties: next })
    }
    const setMortgage = (index, key, value) => {
        const next = [...newMortgages]
        next[index] = { ...next[index], [key]: value }
        onNewMortgages(next)
    }
    const setGift = (index, key, value) => {
        const next = [...gifts]
        next[index] = { ...next[index], [key]: value }
        onAssets({ ...assets, gifts: next })
    }
    const setEO = (partial) => {
        onDeclaration("equalopportunity", { ...eo, hispanic: eoHispanic, race: eoRace, ...partial })
    }
    const setHispanic = (key, value) => {
        onDeclaration("equalopportunity", { ...eo, race: eoRace, hispanic: { ...eoHispanic, [key]: value } })
    }
    const setRace = (key, value) => {
        let race = { ...eoRace, [key]: value }
        if (key === "notfurnished" && value) {
            race = {
                ...eoRace,
                asian: false,
                pacific: false,
                americanindian: false,
                black: false,
                white: false,
                notfurnished: true,
                indiantribe: eoRace.indiantribe || "",
                asiandetails: eoRace.asiandetails || {},
                pacificdetails: eoRace.pacificdetails || {},
            }
        } else if (key !== "notfurnished" && key !== "asiandetails" && key !== "pacificdetails" && key !== "indiantribe" && value) {
            race = { ...race, notfurnished: false }
        }
        onDeclaration("equalopportunity", { ...eo, hispanic: eoHispanic, race })
    }
    const formatMilitaryDate = (raw) => {
        if (!/^[\d/]*$/.test(raw || "")) return military.projectedexpirationdate || ""
        const digits = `${raw}`.replace(/\D/g, "").slice(0, 8)
        if (digits.length <= 2) return digits
        if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
    }
    const setMilitary = (key, value) => {
        onDeclaration("militaryservice", { ...military, [key]: value })
    }

    return (
        <>
            <div id="1c">
                <div className="form1003Title">
                    1c. Additional Employment/Self-Employment and Income
                </div>
                {otherJobs.map((emp, i) => {
                    const setField = (key, value) => onEmploymentItem("otheroccupations", i, { [key]: value })
                    const setIncomeOrLoss = (value) => onEmploymentItem("otheroccupations", i, {
                        incomeorloss: value,
                        incomeorlossperiod: "monthly",
                    })
                    const setIncome = (key, value) => onEmploymentItem("otheroccupations", i, {
                        income: {
                            [key]: value,
                            [`${key}monthly`]: "monthly",
                        },
                    })
                    const setSelfEmployed = (checked) => onEmploymentItem("otheroccupations", i, {
                        selfemployed: checked,
                        percentownership: checked ? "24" : "",
                    })
                    const setOwnershipShare = (choice) => {
                        const orgVal = emp.percentownership
                        let val = ""
                        if (Number(orgVal) < 25 && choice === "less") val = orgVal
                        else if (Number(orgVal) > 24 && choice === "more") val = orgVal
                        else val = choice === "less" ? "24" : "25"
                        setField("percentownership", val)
                    }
                    const total = ["base", "overtime", "bonuses", "commissions", "military", "other"].reduce((sum, key) => {
                        const n = parseFloat(displayMonthlyIncome(emp.income, key))
                        return sum + (Number.isNaN(n) ? 0 : n)
                    }, 0)
                    return (
                        <div className="p-4 removable" key={emp.key || i}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ flex: 1, textAlign: "right", marginRight: 20 }}>
                                    <IncomeCalcBtn
                                        emp={emp}
                                        clipId={`clip0_v2_1c_${i}`}
                                        onClick={() => onOpenIncomeCalculation?.("1c", i, emp)}
                                    />
                                </div>
                                <div className="text-right" style={{ display: "flex", alignItems: "center" }}>
                                    <DeleteControl
                                        square
                                        confirm={false}
                                        onConfirm={() => onEmploymentLists("otheroccupations", otherJobs.filter((_, idx) => idx !== i))}
                                    />
                                </div>
                            </div>
                            <Row>
                                <Col sm={6} className="mb-4"><TextField label="Employer or business name" value={emp.employername} onChange={(v) => setField("employername", v)} /></Col>
                                <Col sm={6} className="mb-4"><TextField label="Phone" value={com.fixPhoneInput(emp.phone)} onChange={(v) => setField("phone", com.fixPhoneInput(v))} /></Col>
                            </Row>
                            <StructuredAddressField
                                id={`v2-other-employer-address-${participant.partyId}-${i}`}
                                label="Address"
                                address={{ street: emp.employerstreet || emp.employeraddress, unit: emp.employerunit, state: emp.employerstate, county: emp.employercounty, city: emp.employercity, zip: emp.employerzip }}
                                onChange={(field, value) => setField(`employer${field}`, value)}
                                onPlace={(parsed) => {
                                    setField("employerstreet", parsed.street)
                                    setField("employercity", parsed.city)
                                    setField("employerstate", parsed.state)
                                    setField("employercounty", parsed.county)
                                    setField("employerzip", parsed.zip)
                                }}
                                loanLimits={loanLimits}
                            />
                            <Row>
                                <Col sm={6} className="mb-4"><TextField label="Position or title" value={emp.positiontitle} onChange={(v) => setField("positiontitle", v)} /></Col>
                                <Col sm={6} className="mb-4">
                                    <Field label="Start date">
                                        <ChromeDatePicker
                                            size="large"
                                            style={{ width: "100%" }}
                                            value={parseOccupationDate(emp.from)}
                                            format={{ format: "MM/DD/YYYY", type: "mask" }}
                                            onChange={(_, s) => setField("from", s)}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col>
                                    <Field label="How long have you been in this line of work?">
                                        <DurationFields
                                            years={emp.yearsatlineofwork}
                                            months={emp.monthsatlineofwork}
                                            onYearsChange={(v) => setField("yearsatlineofwork", v)}
                                            onMonthsChange={(v) => setField("monthsatlineofwork", v)}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col>
                                    <ChromeCheckbox className="fontWeight500" checked={!!emp.employedbyagent} onChange={(e) => setField("employedbyagent", e.target.checked)}>
                                        You are employed by a family member, property seller, real estate agent, or other party to the transaction.
                                    </ChromeCheckbox>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col>
                                    <ChromeCheckbox className="fontWeight500" checked={!!emp.selfemployed} onChange={(e) => setSelfEmployed(e.target.checked)}>
                                        Check if you are the business owner or self-employed
                                    </ChromeCheckbox>
                                </Col>
                            </Row>
                            {emp.selfemployed ? (
                                <>
                                    <Row>
                                        <Col sm={12} className="mb-4">
                                            <Field label="I have an ownership share of">
                                                <ChromeRadioGroup
                                                    value={ownershipShareValue(emp.percentownership)}
                                                    onChange={(e) => setOwnershipShare(e.target.value)}
                                                    options={[
                                                        { value: "less", label: "less than 25%" },
                                                        { value: "more", label: "25% or more" },
                                                    ]}
                                                >
                                                    <div style={{ display: "flex", gap: 10, width: "100%" }}>
                                                        <div style={{ border: "1px solid #ececec", borderRadius: 4, padding: "0 10px", backgroundColor: "#fcfcfc", height: 44, display: "flex", alignItems: "center", justifyContent: "left", flex: 1 }}>
                                                            <AntRadio value="less">less than 25%</AntRadio>
                                                        </div>
                                                        <div style={{ border: "1px solid #ececec", borderRadius: 4, padding: "0 10px", backgroundColor: "#fcfcfc", height: 44, display: "flex", alignItems: "center", justifyContent: "left", flex: 1 }}>
                                                            <AntRadio value="more">25% or more</AntRadio>
                                                        </div>
                                                    </div>
                                                </ChromeRadioGroup>
                                            </Field>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm={6} className="mb-4">
                                            <MoneyTextField label="Monthly Income (or Loss)" value={displayMonthlyIncomeOrLoss(emp)} onChange={(v) => setIncomeOrLoss(v)} />
                                        </Col>
                                    </Row>
                                </>
                            ) : (
                                <>
                                    <hr className="form1003Divider" />
                                    <Row className="mb-4">
                                        <Col>
                                            <Field label="Gross monthly income">
                                                <Row>
                                                    <Col sm={6} className="mb-3"><MoneyPrefixField prefix="Base" suffix="/ month" value={displayMonthlyIncome(emp.income, "base")} onChange={(v) => setIncome("base", v)} /></Col>
                                                    <Col sm={6} className="mb-3"><MoneyPrefixField prefix="Overtime" suffix="/ month" value={displayMonthlyIncome(emp.income, "overtime")} onChange={(v) => setIncome("overtime", v)} /></Col>
                                                </Row>
                                            </Field>
                                        </Col>
                                    </Row>
                                    <Row className="mb-4">
                                        <Col>
                                            <Row>
                                                <Col sm={6} className="mb-3"><MoneyPrefixField prefix="Bonus" suffix="/ month" value={displayMonthlyIncome(emp.income, "bonuses")} onChange={(v) => setIncome("bonuses", v)} /></Col>
                                                <Col sm={6} className="mb-3"><MoneyPrefixField prefix="Commission" suffix="/ month" value={displayMonthlyIncome(emp.income, "commissions")} onChange={(v) => setIncome("commissions", v)} /></Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    <Row className="mb-4">
                                        <Col>
                                            <Row>
                                                <Col sm={6} className="mb-3"><MoneyPrefixField prefix="Military" suffix="/ month" value={displayMonthlyIncome(emp.income, "military")} onChange={(v) => setIncome("military", v)} /></Col>
                                                <Col sm={6} className="mb-3"><MoneyPrefixField prefix="Other" suffix="/ month" value={displayMonthlyIncome(emp.income, "other")} onChange={(v) => setIncome("other", v)} /></Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    <Row className="mb-4">
                                        <Col sm={6}>
                                            <PrefixField label="Total income" prefix="$" suffix="/ month" disabled value={formatMonthlyMoney(total)} />
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </div>
                    )
                })}
                <AddLink onClick={() => onEmploymentLists("otheroccupations", [...otherJobs, emptyJob()])}>Add additional income</AddLink>
            </div>

            <div id="1d">
                <div className="form1003Title">
                    1d. Previous Employment/Self-Employment and Income
                </div>
                {prevJobs.map((emp, i) => {
                    const setField = (key, value) => onEmploymentItem("previousoccupations", i, { [key]: value })
                    const displayIncome = (() => {
                        if (emp.income === "" || emp.income == null) return ""
                        const period = `${emp.incomeperiod || ""}`.toLowerCase()
                        // Only convert when explicitly yearly; keep raw drafts like "1200." otherwise.
                        if (period !== "yearly") return String(emp.income)
                        const n = parseFloat(emp.income)
                        if (Number.isNaN(n)) return String(emp.income)
                        const monthly = n / 12
                        return Number.isInteger(monthly) ? String(monthly) : String(Math.round(monthly * 100) / 100)
                    })()
                    return (
                        <div className="p-4 removable" key={emp.key || i}>
                            <div className="text-right">
                                <DeleteControl
                                    square
                                    confirm={false}
                                    onConfirm={() => onEmploymentLists("previousoccupations", prevJobs.filter((_, idx) => idx !== i))}
                                />
                            </div>
                            <Row>
                                <Col sm={6} className="mb-4"><TextField label="Employer or business name" value={emp.employername} onChange={(v) => setField("employername", v)} /></Col>
                                <Col sm={6} className="mb-4"><TextField label="Position or title" value={emp.positiontitle} onChange={(v) => setField("positiontitle", v)} /></Col>
                            </Row>
                            <StructuredAddressField
                                id={`v2-prev-employer-address-${participant.partyId}-${i}`}
                                label="Address"
                                address={{ street: emp.employerstreet || emp.employeraddress, unit: emp.employerunit, state: emp.employerstate, county: emp.employercounty, city: emp.employercity, zip: emp.employerzip }}
                                onChange={(field, value) => setField(`employer${field}`, value)}
                                onPlace={(parsed) => {
                                    setField("employerstreet", parsed.street)
                                    setField("employercity", parsed.city)
                                    setField("employerstate", parsed.state)
                                    setField("employercounty", parsed.county)
                                    setField("employerzip", parsed.zip)
                                }}
                                loanLimits={loanLimits}
                            />
                            <Row>
                                <Col sm={6} className="mb-4">
                                    <Field label="From">
                                        <ChromeDatePicker
                                            size="large"
                                            style={{ width: "100%" }}
                                            value={parseOccupationDate(emp.from)}
                                            format={{ format: "MM/DD/YYYY", type: "mask" }}
                                            onChange={(_, s) => setField("from", s)}
                                        />
                                    </Field>
                                </Col>
                                <Col sm={6} className="mb-4">
                                    <Field label="To">
                                        <ChromeDatePicker
                                            size="large"
                                            style={{ width: "100%" }}
                                            value={parseOccupationDate(emp.to)}
                                            format={{ format: "MM/DD/YYYY", type: "mask" }}
                                            onChange={(_, s) => setField("to", s)}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col>
                                    <ChromeCheckbox className="fontWeight500" checked={!!emp.employedbyagent} onChange={(e) => setField("employedbyagent", e.target.checked)}>
                                        You are employed by a family member, property seller, real estate agent, or other party to the transaction.
                                    </ChromeCheckbox>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col>
                                    <ChromeCheckbox className="fontWeight500" checked={!!emp.selfemployed} onChange={(e) => setField("selfemployed", e.target.checked)}>
                                        Check if you are the Business Owner or Self-Employed
                                    </ChromeCheckbox>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <MoneyPrefixField
                                        label="Income"
                                        prefix="$"
                                        suffix="/ month"
                                        value={displayIncome}
                                        onChange={(v) => onEmploymentItem("previousoccupations", i, { income: v, incomeperiod: "monthly" })}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )
                })}
                <AddLink onClick={() => onEmploymentLists("previousoccupations", [...prevJobs, emptyPrevJob()])}>Add previous income</AddLink>
            </div>

            <div id="1e">
                <div className="form1003Title mb-4">1e. Income from Other Sources</div>
                {(otherIncome || []).map((item, i) => (
                    <Row key={item.key || i} className="align-items-center">
                        <Col className="mx-2 my-2">
                            {editMode ? (
                                <div className="form1003-other-income-row">
                                    <div className="form1003-other-income-type">
                                        <ChromeSelect
                                            size="large"
                                            style={{ width: "100%" }}
                                            value={item.incometype || "Other"}
                                            options={incomeTypes}
                                            onChange={(v) => {
                                                const desc = (com.othersouresofincome || []).find((row) => row[0] === v)?.[1] || ""
                                                onOtherIncomeItem(i, { incometype: v, description: desc })
                                            }}
                                        />
                                    </div>
                                    <div className="form1003-other-income-amount">
                                        <Input
                                            size="large"
                                            style={{ width: "100%" }}
                                            prefix="$"
                                            suffix="/ month"
                                            value={item.amount}
                                            onChange={(e) => onOtherIncomeItem(i, { amount: moneyTypingValue(e.target.value, 2, true) })}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <FormPreview title={optionLabel(incomeTypes, item.incometype || "Other")} content={`$ ${item.amount || ""} / month`} />
                            )}
                        </Col>
                        <Col xs="auto" className="text-left d-flex align-items-center">
                            <DeleteControl
                                confirm={false}
                                onConfirm={() => onOtherIncome((otherIncome || []).filter((_, idx) => idx !== i))}
                            />
                        </Col>
                    </Row>
                ))}
                {(!otherIncome || otherIncome.length < 3) && (
                    <AddLink onClick={() => onOtherIncome([...(otherIncome || []), emptyOtherIncome()])}>
                        Add income from other resources
                    </AddLink>
                )}
            </div>

            <SectionIntro id="s2" title="Section 2: Financial Information — Assets and Liabilities" text="This section asks about things you own that are worth money and that you want considered to qualify for this loan. It then asks about your liabilities (or debts) that you pay each month, such as credit cards, alimony, or other expenses." />

            <div id="2a">
                <div className="form1003Title">2a. Assets – Bank Accounts, Retirement, and Other Accounts You Have</div>
                <div className="p-4">
                    {accounts.map((item, i) => (
                        <div className="removable p-3 mb-3" key={item.key || i}>
                            <div className="text-right">
                                <DeleteControl onConfirm={() => onAssets({ ...assets, accounts: accounts.filter((_, idx) => idx !== i) })} />
                            </div>
                            <Row>
                                <Col className="mb-3">
                                    <Field label="ACCOUNT TYPE">
                                        <ChromeSelect
                                            style={{ width: "100%" }}
                                            value={item.accountType || "CheckingAccount"}
                                            options={withCurrentOption(accountTypeOptions, item.accountType)}
                                            onChange={(v) => onAssetItem("accounts", i, { accountType: v })}
                                        />
                                    </Field>
                                </Col>
                                <Col className="mb-3"><TextField label="FINANCIAL INSTITUTION" value={item.bankName} onChange={(v) => onAssetItem("accounts", i, { bankName: v })} /></Col>
                                <Col className="mb-3"><TextField label="ACCOUNT NUMBER" value={item.account} onChange={(v) => onAssetItem("accounts", i, { account: v })} /></Col>
                            </Row>
                            <Row>
                                <Col className="mb-3"><SectionAmountField label="CASH/MARKET VALUE" prefix="$" value={item.balance} normalizeOnBlur="dollar" onChange={(v) => onAssetItem("accounts", i, { balance: v })} /></Col>
                                {showOwnership && (
                                    <OwnershipFields {...ownershipProps(item, (patch) => onAssetItem("accounts", i, patch))} />
                                )}
                            </Row>
                        </div>
                    ))}
                    <AddLink onClick={() => onAssets({ ...assets, accounts: [...accounts, emptyAccount(currentPartyId)] })}>Add financial accounts</AddLink>
                </div>
            </div>

            <div id="2b">
                <div className="form1003Title">2b. Other Assets and Credits You Have</div>
                <div className="p-4">
                    {otherAssets.map((item, i) => (
                        <div className="removable p-3 mb-3" key={item.key || i}>
                            <div className="text-right">
                                <DeleteControl onConfirm={() => onAssets({ ...assets, otherAssets: otherAssets.filter((_, idx) => idx !== i) })} />
                            </div>
                            <Row>
                                <Col className="mb-3">
                                    <Field label="ASSET OR CREDIT TYPE">
                                        <ChromeSelect
                                            style={{ width: "100%" }}
                                            value={item.assetType || ""}
                                            options={[{ value: "", label: "Select type" }, ...withCurrentOption(otherAssetTypeOptions, item.assetType)]}
                                            onChange={(v) => onAssetItem("otherAssets", i, { assetType: v, description: "" })}
                                        />
                                    </Field>
                                </Col>
                                <Col className="mb-3"><SectionAmountField label="CASH/MARKET VALUE" prefix="$" value={item.value} normalizeOnBlur="dollar" onChange={(v) => onAssetItem("otherAssets", i, { value: v })} /></Col>
                                {showOwnership && (
                                    <OwnershipFields {...ownershipProps(item, (patch) => onAssetItem("otherAssets", i, patch))} />
                                )}
                            </Row>
                        </div>
                    ))}
                    <AddLink onClick={() => onAssets({ ...assets, otherAssets: [...otherAssets, emptyOtherAsset(currentPartyId)] })}>Add assets or credits</AddLink>
                </div>
            </div>

            <div id="2c">
                <div className="form1003Title liability-title d-flex justify-content-between align-items-center">
                    <span>2c. Liabilities – Credit Cards, Other Debts, and Leases that You Owe</span>
                    <CreditImportButton
                        sectionType="section2c"
                        application={application}
                        participantPartyId={participant.partyId}
                        hasAvailableCreditReport={hasAvailableCreditReport}
                        creditReportCreatedAt={creditReportCreatedAt}
                        onOpen={onOpenCreditImport}
                    />
                </div>
                <div className="p-4">
                    {loans.map((item, i) => (
                        <div className="removable p-3 mb-3" key={item.key || i}>
                            <div className="text-right">
                                <DeleteControl onConfirm={() => removeLiability(i)} />
                            </div>
                            <Row>
                                <Col className="mb-3">
                                    <div style={{ maxWidth: "100%", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                        <Field label="Category">
                                            <ChromeSelect
                                                style={{ width: "100%" }}
                                                value={item.creditLoanType || ""}
                                                options={[{ value: "", label: "Select" }, ...withCurrentOption(liabilityTypeOptions, item.creditLoanType)]}
                                                onChange={(v) => onLiabilityTypeChange(i, v)}
                                            />
                                        </Field>
                                    </div>
                                </Col>
                                <Col className="mb-3"><TextField label="Company Name" value={item.name} onChange={(v) => onLinkedLiabilityItem("loans", i, { name: v })} /></Col>
                                <Col className="mb-3"><TextField label="Account Number" value={item.accountNumber} onChange={(v) => onLinkedLiabilityItem("loans", i, { accountNumber: v })} /></Col>
                            </Row>
                            <Row>
                                <Col className="mb-3"><SectionAmountField label="Unpaid Balance" prefix="$" value={item.remainingBalance} normalizeOnBlur="dollar" onChange={(v) => onLinkedLiabilityItem("loans", i, { remainingBalance: v })} /></Col>
                                <Col className="mb-3"><SectionAmountField label="Mo. Payment" prefix="$" suffix="mo" value={item.monthlyPayment} normalizeOnBlur="dollar" onChange={(v) => onLinkedLiabilityItem("loans", i, { monthlyPayment: v })} /></Col>
                                {showOwnership && (
                                    <OwnershipFields {...ownershipProps(item, (patch) => onLinkedLiabilityItem("loans", i, patch))} />
                                )}
                            </Row>
                            <Row>
                                <Col className="mb-3">
                                    <Field label="Paid off at/before closing">
                                        <ChromeSelect
                                            style={{ width: "100%" }}
                                            value={item.paidOffAtClosing || ""}
                                            options={[{ value: "", label: "Select" }, ...paidOffOptions]}
                                            onChange={(v) => onLinkedLiabilityItem("loans", i, { paidOffAtClosing: v })}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                        </div>
                    ))}
                    <AddLink onClick={() => onLiabilities({ loans: [...loans, emptyLoan(currentPartyId)] })}>Add liability</AddLink>
                </div>
            </div>

            <div id="2d">
                <div className="form1003Title">2d. Other Liabilities and Expenses</div>
                <div className="p-4">
                    {expenses.map((item, i) => (
                        <div className="removable p-3 mb-3" key={item.key || i}>
                            <div className="text-right">
                                <DeleteControl onConfirm={() => onLiabilities({ additionalExpenses: expenses.filter((_, idx) => idx !== i) })} />
                            </div>
                            <Row>
                                <Col className="mb-3">
                                    <Field label="TYPE">
                                        <ChromeSelect
                                            style={{ width: "100%" }}
                                            value={item.alimonyorsupport || "alimony"}
                                            options={withCurrentOption(expenseTypeOptions, item.alimonyorsupport)}
                                            onChange={(v) => onLiabilityItem("additionalExpenses", i, { alimonyorsupport: v })}
                                        />
                                    </Field>
                                </Col>
                                <Col className="mb-3"><SectionAmountField label="MONTHLY PAYMENT" prefix="$" value={item.amount} normalizeOnBlur="dollar" onChange={(v) => onLiabilityItem("additionalExpenses", i, { amount: v })} /></Col>
                            </Row>
                        </div>
                    ))}
                    {expenses.length < 3 && (
                        <AddLink onClick={() => onLiabilities({ additionalExpenses: [...expenses, emptyExpense()] })}>Add other liability</AddLink>
                    )}
                </div>
            </div>

            <SectionIntro id="s3" title="Section 3: Financial Information — Real Estate" text="This section asks you to list all properties you currently own and what you owe on them." />

            <div id="3a">
                <div className="form1003Title liability-title d-flex justify-content-between align-items-center">
                    <span>3a. Property You Own</span>
                    {(subjectIn3a ? canEditSubject : true) ? (
                        <CreditImportButton
                            sectionType="section3"
                            application={application}
                            participantPartyId={participant.partyId}
                            reoIndex={0}
                            isSubjectProperty={subjectIn3a}
                            hasAvailableCreditReport={hasAvailableCreditReport}
                            creditReportCreatedAt={creditReportCreatedAt}
                            onOpen={onOpenCreditImport}
                        />
                    ) : null}
                </div>
                <div className="form1003SectionNote">
                    Include any property you&apos;re obligated on a mortgage for, even if you&apos;re not on the title.
                </div>
                <div className="p-4">
                    {mapOwnedAsPurchase ? (
                        <Row className="mb-4">
                            <Col>
                                <ChromeCheckbox
                                    checked={!firstOwned}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setShowOwnPropertyWarning1(false)
                                            setShowOwnPropertyWarning2(false)
                                            onRealEstate({ properties: [] })
                                        } else {
                                            onRealEstate({ properties: reo.length ? reo : [emptyOwned(currentPartyId)] })
                                        }
                                    }}
                                >
                                    I do not own any real estate
                                </ChromeCheckbox>
                            </Col>
                        </Row>
                    ) : null}
                    {subjectIn3a ? (
                        <ReoCard
                            item={{
                                ...property,
                                ownerPartyIds: subjectOwnerIds.length
                                    ? subjectOwnerIds
                                    : (primaryPartyId ? [primaryPartyId] : []),
                                homePartyId: subjectHomePartyId,
                            }}
                            isSubject
                            readOnly={!canEditSubject}
                            ownership={showOwnership ? {
                                show: true,
                                currentPartyId,
                                otherParticipants,
                                canEdit: canEditSubject,
                                onChange: (patch) => onSubjectMerge({
                                    ...patch,
                                    homePartyId: subjectHomePartyId,
                                }),
                            } : null}
                            onField={writeSubjectField}
                            onFields={writeSubjectFields}
                            onLoans={(nextLoans) => onPropertyLoansChange(nextLoans, { isSubject: true })}
                            loanLimits={loanLimits}
                        />
                    ) : firstOwned ? (
                        <ReoCard
                            item={firstOwned}
                            index={0}
                            extraLabel={firstOwned.isCurrent ? "Current residence" : ""}
                            ownership={reoOwnership(firstOwned, (patch) => patchOwned(0, patch))}
                            onField={(key, value) => patchOwned(0, { [key]: value })}
                            onFields={(fields) => patchOwned(0, fields)}
                            onLoans={(nextLoans) => onPropertyLoansChange(nextLoans, { reoIndex: 0 })}
                            loanLimits={loanLimits}
                        />
                    ) : null}
                </div>
            </div>

            <div id="3b">
                <div className="form1003Title liability-title d-flex justify-content-between align-items-center">
                    <span>3b. Additional Property</span>
                    {!doesNotApply3b ? (
                        <CreditImportButton
                            sectionType="section3"
                            application={application}
                            participantPartyId={participant.partyId}
                            reoIndex={extraIndex}
                            hasAvailableCreditReport={hasAvailableCreditReport}
                            creditReportCreatedAt={creditReportCreatedAt}
                            onOpen={onOpenCreditImport}
                        />
                    ) : null}
                </div>
                <div className="p-4">
                    <Row className="mb-4">
                        <Col>
                            <ChromeCheckbox
                                className="fontWeight500"
                                checked={doesNotApply3b}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setShowOwnPropertyWarning1(false)
                                        setShowOwnPropertyWarning2(false)
                                        onRealEstate({ properties: reo.slice(0, extraIndex) })
                                        return
                                    }
                                    if (mapOwnedAsPurchase) {
                                        if (reo.length === 0) {
                                            onRealEstate({ properties: [emptyOwned(currentPartyId), emptyOwned(currentPartyId)] })
                                            setShowOwnPropertyWarning1(true)
                                        } else if (reo.length === 1) {
                                            onRealEstate({ properties: [...reo, emptyOwned(currentPartyId)] })
                                            setShowOwnPropertyWarning1(false)
                                        }
                                    } else if (reo.length === 0) {
                                        onRealEstate({ properties: [emptyOwned(currentPartyId)] })
                                        setShowOwnPropertyWarning1(false)
                                    }
                                }}
                            >
                                Does not apply
                            </ChromeCheckbox>
                        </Col>
                    </Row>
                    {showOwnPropertyWarning1 ? (
                        <Row>
                            <Col className="mb-3">
                                <Alert
                                    message="Warning"
                                    description="Please fill the property information in section 3a first"
                                    type="warning"
                                    showIcon
                                    closable
                                    onClose={() => setShowOwnPropertyWarning1(false)}
                                />
                            </Col>
                        </Row>
                    ) : null}
                    {extraOwnedItem ? (
                        <ReoCard
                            key={extraOwnedItem.key || extraIndex}
                            item={extraOwnedItem}
                            index={extraIndex}
                            extraLabel={extraOwnedItem.isCurrent ? "Current residence" : ""}
                            ownership={reoOwnership(extraOwnedItem, (patch) => patchOwned(extraIndex, patch))}
                            onField={(key, value) => patchOwned(extraIndex, { [key]: value })}
                            onFields={(fields) => patchOwned(extraIndex, fields)}
                            onLoans={(nextLoans) => onPropertyLoansChange(nextLoans, { reoIndex: extraIndex })}
                            loanLimits={loanLimits}
                        />
                    ) : null}
                </div>
            </div>

            <div id="3c">
                <div className="form1003Title liability-title d-flex justify-content-between align-items-center">
                    <span>3c. Additional Property</span>
                    {!doesNotApply3c ? (
                        <CreditImportButton
                            sectionType="section3"
                            application={application}
                            participantPartyId={participant.partyId}
                            reoIndex={thirdIndex}
                            hasAvailableCreditReport={hasAvailableCreditReport}
                            creditReportCreatedAt={creditReportCreatedAt}
                            onOpen={onOpenCreditImport}
                        />
                    ) : null}
                </div>
                <div className="p-4">
                    <Row className="mb-4">
                        <Col>
                            <ChromeCheckbox
                                className="fontWeight500"
                                checked={doesNotApply3c}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setShowOwnPropertyWarning1(false)
                                        setShowOwnPropertyWarning2(false)
                                        onRealEstate({ properties: reo.slice(0, thirdIndex) })
                                        return
                                    }
                                    if (mapOwnedAsPurchase) {
                                        if (reo.length === 0) {
                                            onRealEstate({ properties: padOwnedTo(3) })
                                            setShowOwnPropertyWarning1(true)
                                            setShowOwnPropertyWarning2(true)
                                        } else if (reo.length === 1) {
                                            onRealEstate({ properties: padOwnedTo(3) })
                                            setShowOwnPropertyWarning1(false)
                                            setShowOwnPropertyWarning2(true)
                                        } else if (reo.length === 2) {
                                            onRealEstate({ properties: padOwnedTo(3) })
                                            setShowOwnPropertyWarning1(false)
                                            setShowOwnPropertyWarning2(false)
                                        }
                                    } else if (reo.length === 0) {
                                        onRealEstate({ properties: padOwnedTo(2) })
                                        setShowOwnPropertyWarning1(false)
                                        setShowOwnPropertyWarning2(true)
                                    } else if (reo.length === 1) {
                                        onRealEstate({ properties: padOwnedTo(2) })
                                        setShowOwnPropertyWarning1(false)
                                        setShowOwnPropertyWarning2(false)
                                    }
                                }}
                            >
                                Does not apply
                            </ChromeCheckbox>
                        </Col>
                    </Row>
                    {showOwnPropertyWarning2 ? (
                        <Row>
                            <Col className="mb-3">
                                <Alert
                                    message="Warning"
                                    description="Please fill the property information in section 3a and 3b first"
                                    type="warning"
                                    showIcon
                                    closable
                                    onClose={() => setShowOwnPropertyWarning2(false)}
                                />
                            </Col>
                        </Row>
                    ) : null}
                    {thirdOwnedItem ? (
                        <ReoCard
                            key={thirdOwnedItem.key || thirdIndex}
                            item={thirdOwnedItem}
                            index={thirdIndex}
                            ownership={reoOwnership(thirdOwnedItem, (patch) => patchOwned(thirdIndex, patch))}
                            onField={(key, value) => patchOwned(thirdIndex, { [key]: value })}
                            onFields={(fields) => patchOwned(thirdIndex, fields)}
                            onLoans={(nextLoans) => onPropertyLoansChange(nextLoans, { reoIndex: thirdIndex })}
                            loanLimits={loanLimits}
                        />
                    ) : null}
                </div>
            </div>

            <div id="3extra">
                {moreOwned.map((item, extraOffset) => {
                    const i = mapOwnedAsPurchase ? extraOffset + 3 : extraOffset + 2
                    const extraNumber = mapOwnedAsPurchase ? i - 2 : i - 1
                    return (
                        <div key={item.key || i}>
                            <div className="form1003Title d-flex justify-content-between">
                                <div>Extra Property {extraNumber}</div>
                                <div className="text-right">
                                    <DeleteControl
                                        square
                                        confirm={false}
                                        onConfirm={() => onRealEstate({ properties: reo.filter((_, idx) => idx !== i) })}
                                    />
                                </div>
                            </div>
                            <div className="p-4">
                                <ReoCard
                                    item={item}
                                    index={i}
                                    ownership={reoOwnership(item, (patch) => patchOwned(i, patch))}
                                    onField={(key, value) => patchOwned(i, { [key]: value })}
                                    onFields={(fields) => patchOwned(i, fields)}
                                    onLoans={(nextLoans) => onPropertyLoansChange(nextLoans, { reoIndex: i })}
                                    loanLimits={loanLimits}
                                />
                            </div>
                        </div>
                    )
                })}
                {sharedFromOthers.map((item, index) => (
                    <div key={item.key || `shared-${index}`}>
                        <div className="form1003Title">Shared with you</div>
                        <div className="p-4">
                            {showNonOwnSharedOwnerWarning
                                && shouldShowNonOwnSharedOwnerWarningOnProperty(participant, item) ? (
                                <NonOwnSharedOwnerWarningBanner
                                    message={nonOwnSharedOwnerWarningText(participantDisplayName(participant), "property")}
                                    onDismiss={onDismissNonOwnSharedOwnerWarning}
                                />
                            ) : null}
                            <ReoCard
                                item={item}
                                index={`shared-${index}`}
                                readOnly
                                ownership={showOwnership ? {
                                    show: true,
                                    currentPartyId,
                                    otherParticipants,
                                    canEdit: false,
                                    onChange: () => {},
                                } : null}
                                onField={() => {}}
                                onFields={() => {}}
                                onLoans={() => {}}
                                loanLimits={loanLimits}
                            />
                        </div>
                    </div>
                ))}
                <AddLink onClick={() => onRealEstate({ properties: [...reo, emptyOwned(currentPartyId)] })}>Add property</AddLink>
            </div>

            <SectionIntro id="s4" title="Section 4: Loan and Property Information" text="This section asks about the loan’s purpose and the property you want to purchase or refinance." />

            <div id="4a">
                <div className="form1003Title">4a. Loan and Property Information</div>
                <div className="p-4">
                    <Row>
                        <Col sm={6} className="mb-4">
                            <Field label="Loan purpose">
                                <ChromeSelect style={{ width: "100%" }} value={property.purpose || ""} options={[
                                    { value: "", label: "Select loan purpose" },
                                    { value: "purchase", label: "Purchase" },
                                    { value: "refinance", label: "Rate/Term refinance" },
                                    { value: "cashoutrefinance", label: "Cash-out refinance" },
                                ]} onChange={(v) => {
                                    const fields = v === "purchase" ? { purpose: v, hascontract: true } : { purpose: v }
                                    onPropertyFields(fields)
                                    clearPurchaseRentalIfHidden(fields)
                                    // Recompute derived selection fields for the new purpose so
                                    // Rate recommendation does not keep a stale downpayment/paydown.
                                    const size = numOrZero(selection.loansize)
                                    const balance = numOrZero(application?.loanbalance)
                                    if (v === "purchase") {
                                        const price = numOrZero(property.salesprice)
                                        onSelection("downpayment", price >= size ? String(price - size) : "0")
                                    } else if (v === "refinance") {
                                        onSelection("paydown", balance >= size ? String(balance - size) : "0")
                                    } else if (v === "cashoutrefinance") {
                                        const price = numOrZero(property.appraisal)
                                        onSelection("cashout", (price >= size && size >= balance) ? String(size - balance) : "0")
                                    }
                                }} />
                            </Field>
                        </Col>
                        <Col sm={6} className="mb-4">
                            <Field label="Intended occupancy">
                                <ChromeSelect style={{ width: "100%" }} value={property.occupancy || ""} options={[
                                    { value: "principal", label: "Primary Residence" },
                                    { value: "secondhome", label: "Second Home" },
                                    { value: "investment", label: "Investment" },
                                    { value: "fhasecond", label: "FHA Secondary Residence" },
                                ]} onChange={(v) => {
                                    onProperty("occupancy", v)
                                    clearPurchaseRentalIfHidden({ occupancy: v })
                                    clearSubjectRentalIfHidden({ occupancy: v })
                                }} />
                            </Field>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6} className="mb-4">
                            <SectionAmountField
                                label="Property value"
                                prefix="$"
                                value={subjectValue}
                                normalizeOnBlur="dollar"
                                onChange={(v) => {
                                    if (property.purpose === "purchase") {
                                        onProperty("salesprice", v)
                                        const size = numOrZero(selection.loansize)
                                        const price = numOrZero(v)
                                        onSelection("downpayment", price >= size ? String(price - size) : "0")
                                        syncLoanRelated({ salesprice: v, skipRelated: true })
                                    } else {
                                        onProperty("appraisal", v)
                                        syncLoanRelated({ appraisal: v, skipRelated: true })
                                    }
                                }}
                            />
                        </Col>
                        {(property.purpose === "refinance" || property.purpose === "cashoutrefinance") ? (
                            <Col sm={6} className="mb-4">
                                <SectionAmountField
                                    label="Current balance"
                                    prefix="$"
                                    value={application?.loanbalance}
                                    normalizeOnBlur="dollar"
                                    onChange={(v) => {
                                        if (onLoanBalance) onLoanBalance(v)
                                        syncLoanRelated({ loanbalance: v })
                                    }}
                                />
                            </Col>
                        ) : null}
                        <Col sm={6} className="mb-4">
                            <SectionAmountField
                                label="Loan amount"
                                prefix="$"
                                value={selection.loansize}
                                error={loanAmountError}
                                normalizeOnBlur="dollar"
                                onChange={(v) => {
                                    onSelection("loansize", v)
                                    syncLoanRelated({ loansize: v })
                                }}
                                onBlur={(e) => validateLoanAmount(e?.target?.value ?? selection.loansize)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6} className="mb-4">
                            <Field label="Property type">
                                <ChromeSelect style={{ width: "100%" }} value={property.propertytype || ""} options={subjectPropertyTypeOptions} onChange={(v) => {
                                    onProperty("propertytype", v)
                                    clearPurchaseRentalIfHidden({ propertytype: v })
                                    clearSubjectRentalIfHidden({ propertytype: v })
                                }} />
                            </Field>
                        </Col>
                        <Col sm={6} className="mb-4"><TextField label="Number of units" value={property.numberofunits} onChange={(v) => onProperty("numberofunits", v)} /></Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            <ChromeCheckbox
                                className="fontWeight500"
                                checked={!!property.buyforsomeoneelse}
                                onChange={(e) => onPropertyFields({ buyforsomeoneelse: e.target.checked })}
                            >
                                Buying the property for someone else
                            </ChromeCheckbox>
                        </Col>
                    </Row>
                    <StructuredAddressField
                        id="v2-subject-property-address"
                        label="Address"
                        address={{
                            street: property.address,
                            unit: property.unit,
                            state: property.state,
                            county: property.county,
                            city: property.city,
                            zip: property.zipcode,
                        }}
                        onChange={(key, value) => onProperty(key === "street" ? "address" : key === "zip" ? "zipcode" : key, value)}
                        onPlace={(parsed) => onPropertyFields({
                            address: parsed.street || "",
                            city: parsed.city,
                            state: parsed.state,
                            zipcode: parsed.zip,
                            county: parsed.county,
                        })}
                        loanLimits={loanLimits}
                    />
                    <div className="mb-4 insurance-and-tax-breakdown">
                        <Row>
                            <Col sm={6} className="mb-4">
                                <MoneyPctField label="Insurance (monthly)" amount={property.insurancemain} propertyValue={subjectValue} onAmount={(v) => setSubjectInsurance({ insurancemain: v })} />
                            </Col>
                            <Col sm={6} className="mb-4">
                                <MoneyPctField label="Tax (monthly)" amount={property.insurancetax} propertyValue={subjectValue} onAmount={(v) => setSubjectInsurance({ insurancetax: v })} />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={6} className="mb-4">
                                <div className="insurance-and-tax-breakdown-label">HOA fee (monthly)</div>
                                <InputNumber
                                    prefix="$"
                                    className="estimated-monthly-payment-input"
                                    style={{ width: "100%" }}
                                    controls={false}
                                    stringMode
                                    value={property.insurancehoa || "0"}
                                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                    parser={(v) => moneyTypingValue(v, 2)}
                                    onChange={(v) => setSubjectInsurance({ insurancehoa: v == null || v === "" ? "" : moneyTypingValue(v, 2) })}
                                />
                            </Col>
                            {needsPMI ? (
                                <Col sm={6} className="mb-4">
                                    <MoneyPctField label="PMI (monthly)" amount={property.insurancepmi} propertyValue={subjectValue} onAmount={(v) => setSubjectInsurance({ insurancepmi: v })} />
                                </Col>
                            ) : null}
                        </Row>
                    </div>
                    <hr className="form1003Divider" />
                    <Row className="mb-4">
                        <Col>
                            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>1. Mixed-use property</div>
                            <YesNoBool
                                question="If you will occupy the property, will you set aside space within the property to operate your own business?"
                                value={property.isMixedUse === true}
                                onChange={(v) => onPropertyFields({ isMixedUse: v })}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>2. Manufactured home</div>
                            <YesNoBool
                                question="Is the property a manufactured home? (e.g., a factory built dwelling built on a permanent chassis)"
                                value={property.isManufacturedHome === true}
                                onChange={(v) => {
                                    const propertytype = v ? "mobilehome" : "singlefamilydetached"
                                    const next = { ...property, propertytype }
                                    const clearPurchase = !showsPurchaseRentalIncome(next)
                                    const clearSubject = !showsSubjectRentalIncome(next)
                                    if (onSubjectMerge) {
                                        onSubjectMerge({
                                            isManufacturedHome: v,
                                            propertytype,
                                            ...(clearPurchase ? {
                                                purchase: {
                                                    expectedgrossmonthlyrentalincome: "",
                                                    expectednetmonthlyrentalincome: "",
                                                },
                                            } : {}),
                                            ...(clearSubject ? {
                                                grossrentalincome: "0",
                                                netrentalincome: "0",
                                            } : {}),
                                        })
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                    {property.isManufacturedHome === true ? (
                        <div>
                            <Row className="mb-4">
                                <Col>
                                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Manufactured home details</div>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={6} className="mb-4">
                                    <TextField label="Manufactured home park full name" value={mobile.mobilehomeparkfullname} onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { mobilehomeparkfullname: v } })} />
                                </Col>
                            </Row>
                            <StructuredAddressField
                                id="v2-park-address"
                                label="Park Address"
                                address={{ street: mobile.parkStreet || mobile.parkAddress, unit: mobile.parkUnit, state: mobile.parkState, county: mobile.parkCounty, city: mobile.parkCity, zip: mobile.parkZip }}
                                onChange={(key, value) => onSubjectMerge?.({ mobilehomeinfo: {
                                    [`park${key.charAt(0).toUpperCase()}${key.slice(1)}`]: value,
                                    ...(key === "street" ? { parkAddress: value } : {}),
                                } })}
                                onPlace={(parsed) => onSubjectMerge?.({ mobilehomeinfo: {
                                    parkAddress: fullAddressFromPlace(parsed),
                                    parkStreet: parsed.street,
                                    parkState: parsed.state,
                                    parkCounty: parsed.county,
                                    parkCity: parsed.city,
                                    parkZip: parsed.zip,
                                } })}
                                loanLimits={loanLimits}
                                optional
                            />
                            <Row>
                                <Col sm={6} className="mb-4"><SectionAmountField label="Monthly space rent" prefix="$" value={mobile.leasedSpaceRentAmount} normalizeOnBlur="dollar" onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { leasedSpaceRentAmount: v } })} /></Col>
                                <Col sm={6} className="mb-4"><TextField label="Space number" value={mobile.space} onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { space: v } })} /></Col>
                            </Row>
                            <Row>
                                <Col sm={6} className="mb-4">
                                    <Field label="Width">
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <Input style={{ width: "49%" }} size="large" suffix="ft" value={mobile.widthft || ""} onChange={(e) => onSubjectMerge?.({ mobilehomeinfo: { widthft: e.target.value } })} />
                                            <Input style={{ width: "49%" }} size="large" suffix="inch" value={mobile.widthinch || ""} onChange={(e) => onSubjectMerge?.({ mobilehomeinfo: { widthinch: e.target.value } })} />
                                        </div>
                                    </Field>
                                </Col>
                                <Col sm={6} className="mb-4">
                                    <Field label="Length">
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <Input style={{ width: "49%" }} size="large" suffix="ft" value={mobile.lengthft || ""} onChange={(e) => onSubjectMerge?.({ mobilehomeinfo: { lengthft: e.target.value } })} />
                                            <Input style={{ width: "49%" }} size="large" suffix="inch" value={mobile.lengthinch || ""} onChange={(e) => onSubjectMerge?.({ mobilehomeinfo: { lengthinch: e.target.value } })} />
                                        </div>
                                    </Field>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={6} className="mb-4">
                                    <Field label="Condition">
                                        <ChromeSelect style={{ width: "100%" }} value={mobile.mobilehomedescription || ""} options={[
                                            { value: "", label: "Select" },
                                            { value: "New", label: "New" },
                                            { value: "Used", label: "Used" },
                                            { value: "Repo", label: "Repo" },
                                        ]} onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { mobilehomedescription: v } })} />
                                    </Field>
                                </Col>
                                <Col sm={6} className="mb-4">
                                    <Field label="Type">
                                        <ChromeSelect style={{ width: "100%" }} value={mobile.mobilehometype || ""} options={[
                                            { value: "", label: "Select" },
                                            { value: "SingleWide", label: "Single Wide" },
                                            { value: "DoubleWide", label: "Double Wide" },
                                            { value: "TripleWide", label: "Triple Wide" },
                                        ]} onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { mobilehometype: v } })} />
                                    </Field>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={6} className="mb-4"><TextField label="Manufacturer make" value={mobile.manufacturerMake} onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { manufacturerMake: v } })} /></Col>
                                <Col sm={6} className="mb-4"><TextField label="Manufacturer model" value={mobile.manufacturerModel} onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { manufacturerModel: v } })} /></Col>
                            </Row>
                            <Row>
                                <Col sm={6} className="mb-4"><TextField label="Year built" value={mobile.year} onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { year: v } })} /></Col>
                                <Col sm={6} className="mb-4">
                                    <Field label="Location">
                                        <ChromeSelect style={{ width: "100%" }} value={mobile.mobilehomelocation || ""} options={[
                                            { value: "", label: "Select" },
                                            { value: "Park", label: "Park" },
                                            { value: "LandorHome", label: "Land/home" },
                                            { value: "CoopPark", label: "Co-op park" },
                                            { value: "SharedPark", label: "Shared park" },
                                        ]} onChange={(v) => onSubjectMerge?.({ mobilehomeinfo: { mobilehomelocation: v } })} />
                                    </Field>
                                </Col>
                            </Row>
                        </div>
                    ) : null}
                </div>
            </div>

            <div id="4b">
                <div className="form1003Title">4b. Other New Mortgage Loans on the Property You are Buying or Refinancing</div>
                <div className="p-4">
                    {newMortgages.map((item, i) => (
                        <div className="removable p-3 mb-3" key={item.key || i}>
                            <div className="text-right">
                                <DeleteControl onConfirm={() => onNewMortgages(newMortgages.filter((_, idx) => idx !== i))} />
                            </div>
                            <Row>
                                <Col sm={6} className="mb-3"><TextField label="Creditor name" value={item.creditor} onChange={(v) => setMortgage(i, "creditor", v)} /></Col>
                                <Col sm={6} className="mb-3">
                                    <Field label="Lien type">
                                        <ChromeSelect
                                            style={{ width: "100%" }}
                                            value={item.lienType || item.lientype || "firstlien"}
                                            options={[
                                                { value: "firstlien", label: "First lien" },
                                                { value: "subordinatelien", label: "Subordinate lien" },
                                            ]}
                                            onChange={(v) => setMortgage(i, "lienType", v)}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={4} className="mb-3"><SectionAmountField label="Monthly amount" prefix="$" suffix="mo" value={item.monthlyAmount || item.monthlyamount} normalizeOnBlur="dollar" onChange={(v) => setMortgage(i, "monthlyAmount", v)} /></Col>
                                <Col sm={4} className="mb-3"><SectionAmountField label="Loan amount" prefix="$" value={item.loanAmount || item.loanamount} normalizeOnBlur="dollar" onChange={(v) => setMortgage(i, "loanAmount", v)} /></Col>
                                <Col sm={4} className="mb-3"><SectionAmountField label="Credit limit" prefix="$" value={item.creditLimit || item.creditlimit} normalizeOnBlur="dollar" onChange={(v) => setMortgage(i, "creditLimit", v)} /></Col>
                            </Row>
                        </div>
                    ))}
                    <AddLink onClick={() => onNewMortgages([...newMortgages, emptyMortgage()])}>Add mortgage loan</AddLink>
                </div>
            </div>

            <div id="4c">
                <div className="form1003Title">4c. Rental Income on the Property You Want to Purchase (For Purchase Only)</div>
                <div className="p-4">
                    <Row className="mb-4">
                        <Col>
                            <ChromeCheckbox
                                className="fontWeight500"
                                checked={property.propertytype === "twotofourfamily"}
                                onChange={(e) => {
                                    const propertytype = e.target.checked ? "twotofourfamily" : "singlefamilydetached"
                                    onProperty("propertytype", propertytype)
                                    clearPurchaseRentalIfHidden({ propertytype })
                                    clearSubjectRentalIfHidden({ propertytype })
                                }}
                            >
                                This is a 2-4 unit property
                            </ChromeCheckbox>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            <ChromeCheckbox
                                className="fontWeight500"
                                checked={property.occupancy === "investment"}
                                onChange={(e) => {
                                    const occupancy = e.target.checked ? "investment" : "principal"
                                    onProperty("occupancy", occupancy)
                                    clearPurchaseRentalIfHidden({ occupancy })
                                    clearSubjectRentalIfHidden({ occupancy })
                                }}
                            >
                                This is a investment property
                            </ChromeCheckbox>
                        </Col>
                    </Row>
                    {showsPurchaseRentalIncome(property) ? (
                        <Row className="mb-4">
                            <Col>
                                <SectionAmountField
                                    label="Expected monthly rental income"
                                    prefix="$"
                                    value={property.purchase?.expectedgrossmonthlyrentalincome}
                                    normalizeOnBlur="dollar"
                                    onChange={(v) => onSubjectMerge?.({ purchase: { expectedgrossmonthlyrentalincome: v } })}
                                />
                            </Col>
                            <Col>
                                <SectionAmountField
                                    label="Expected net monthly income"
                                    prefix="$"
                                    value={property.purchase?.expectednetmonthlyrentalincome}
                                    normalizeOnBlur="dollar"
                                    onChange={(v) => onSubjectMerge?.({ purchase: { expectednetmonthlyrentalincome: v } })}
                                />
                            </Col>
                        </Row>
                    ) : null}
                </div>
            </div>

            <div id="4d">
                <div className="form1003Title">4d. Gifts or Grants You Have Been Given or Will Receive for this Loan</div>
                <div className="p-4">
                    {gifts.map((item, i) => {
                        const sourceValue = item.sourceDescription || item.source || ""
                        return (
                        <div className="removable p-3 mb-3" key={item.key || i}>
                            <div className="text-right">
                                <DeleteControl onConfirm={() => onAssets({ ...assets, gifts: gifts.filter((_, idx) => idx !== i) })} />
                            </div>
                            <Row>
                                <Col sm={4} className="mb-3">
                                    <Field label="Asset or credit type">
                                        <ChromeSelect
                                            style={{ width: "100%" }}
                                            value={item.assetType || item.assettype || ""}
                                            options={[
                                                { value: "", label: "Select" },
                                                { value: "GiftOfCash", label: "Cash Gift" },
                                                { value: "GiftOfPropertyEquity", label: "Gift of Equity" },
                                                { value: "Grant", label: "Grant" },
                                            ]}
                                            onChange={(v) => setGift(i, "assetType", v)}
                                        />
                                    </Field>
                                </Col>
                                <Col sm={4} className="mb-3">
                                    <Field label="Deposited/Not">
                                        <ChromeSelect
                                            style={{ width: "100%" }}
                                            value={item.deposited === false ? "no" : "yes"}
                                            options={[
                                                { value: "yes", label: "Deposited" },
                                                { value: "no", label: "Not Deposited" },
                                            ]}
                                            onChange={(v) => setGift(i, "deposited", v === "yes")}
                                        />
                                    </Field>
                                </Col>
                                <Col sm={4} className="mb-3">
                                    <Field label="Source">
                                        <ChromeSelect
                                            style={{ width: "100%" }}
                                            value={sourceValue}
                                            options={[
                                                { value: "", label: "Select source" },
                                                { value: "CommunityNonprofit", label: "Community Nonprofit" },
                                                { value: "Employer", label: "Employer" },
                                                { value: "FederalAgency", label: "Federal Agency" },
                                                { value: "LocalAgency", label: "Local Agency" },
                                                { value: "StateAgency", label: "State Agency" },
                                                { value: "Lender", label: "Lender" },
                                                { value: "Relative", label: "Relative" },
                                                { value: "ReligiousNonprofit", label: "Religious Nonprofit" },
                                                { value: "UnmarriedPartner", label: "Unmarried Partner" },
                                                { value: "Other", label: "Other" },
                                            ]}
                                            onChange={(v) => {
                                                const next = [...gifts]
                                                next[i] = { ...next[i], source: v, sourceDescription: v }
                                                onAssets({ ...assets, gifts: next })
                                            }}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={4} className="mb-3">
                                    <SectionAmountField label="Cash/market value" prefix="$" value={item.value} normalizeOnBlur="dollar" onChange={(v) => setGift(i, "value", v)} />
                                </Col>
                                {showOwnership ? (
                                    <OwnershipFields
                                        {...ownershipProps(item, (patch) => {
                                            const next = [...gifts]
                                            next[i] = { ...next[i], ...patch }
                                            onAssets({ ...assets, gifts: next })
                                        })}
                                    />
                                ) : null}
                            </Row>
                        </div>
                        )
                    })}
                    <AddLink onClick={() => onAssets({ ...assets, gifts: [...gifts, emptyGift(currentPartyId)] })}>Add assets or credits</AddLink>
                </div>
            </div>

            <SectionIntro id="s5" title="Section 5: Declarations" text="This section asks you specific questions about the property, your funding, and your past financial history." />

            <div id="5a">
                <div className="form1003Title">5a. About this Property and Your Money for this Loan</div>
                <div className="p-4">
                    <Row className="mb-4"><Col><YesNo label="A. Will you occupy the property as your primary residence?" value={declarations.occupyasprimary} onChange={(v) => onDeclaration("occupyasprimary", v)} /></Col></Row>
                    {declarations.occupyasprimary === "yes" ? (
                        <Row className="mb-4"><Col><YesNo label="Have you had an ownership interest in another property in the last three years?" value={declarations.ownershipinterest} onChange={(v) => onDeclaration("ownershipinterest", v)} /></Col></Row>
                    ) : null}
                    {declarations.occupyasprimary === "yes" && declarations.ownershipinterest === "yes" ? (
                        <>
                            <Row className="mb-4">
                                <Col>
                                    <Field label="(1) What type of property did you own: primary residence (PR), FHA secondary residence (SR), second home (SH), or investment property (IP)?">
                                        <ChromeSelect style={{ width: "100%" }} value={declarations.propertyowned || "principal"} options={[
                                            { value: "", label: "..." },
                                            { value: "principal", label: "Primary residence" },
                                            { value: "fhasecond", label: "FHA Secondary Residence" },
                                            { value: "secondhome", label: "Second home" },
                                            { value: "investment", label: "Investment property" },
                                        ]} onChange={(v) => onDeclaration("propertyowned", v)} />
                                    </Field>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col>
                                    <Field label="(2) How did you hold title to the property: by yourself (S), jointly with your spouse (SP), or jointly with another person (O)?">
                                        <ChromeSelect style={{ width: "100%" }} value={declarations.titleheld || "yourself"} options={[
                                            { value: "", label: "..." },
                                            { value: "yourself", label: "By Yourself" },
                                            { value: "jointwithspouse", label: "Jointly with Your Spouse" },
                                            { value: "jointwithother", label: "Jointly with Another Person" },
                                        ]} onChange={(v) => onDeclaration("titleheld", v)} />
                                    </Field>
                                </Col>
                            </Row>
                        </>
                    ) : null}
                    <hr className="form1003Divider" />
                    <Row className="mb-4"><Col><YesNo label="B. If this is a Purchase Transaction: Do you have a family relationship or business affiliation with the seller of the property?" value={declarations.familyrelationship} onChange={(v) => onDeclaration("familyrelationship", v)} /></Col></Row>
                    <hr className="form1003Divider" />
                    <Row className="mb-4"><Col><YesNo label="C. Are you borrowing any money for this real estate transaction or obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?" value={declarations.downpaymentborrowed} onChange={(v) => onDeclaration("downpaymentborrowed", v)} /></Col></Row>
                    {declarations.downpaymentborrowed === "yes" ? (
                        <Row className="mb-4 align-items-center">
                            <Col md={7}>What is the amount of this money?</Col>
                            <Col md={5}><SectionAmountField label="Amount:" prefix="$" value={declarations.moneyborrowed} normalizeOnBlur="dollar" onChange={(v) => onDeclaration("moneyborrowed", v)} /></Col>
                        </Row>
                    ) : null}
                    <hr className="form1003Divider" />
                    <Row className="mb-4"><Col><YesNo label="D. 1. Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?" value={declarations.applyingonanother} onChange={(v) => onDeclaration("applyingonanother", v)} /></Col></Row>
                    <hr className="form1003Divider" />
                    <Row className="mb-4"><Col><YesNo label="D. 2. Have you or will you be applying for any new credit on or before closing this loan that is not disclosed on this application?" value={declarations.applyingnewcredit} onChange={(v) => onDeclaration("applyingnewcredit", v)} /></Col></Row>
                    <hr className="form1003Divider" />
                    <Row className="mb-4"><Col><YesNo label="E. Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through your property taxes (e.g., the Property Assessed Clean Energy Program)?" value={declarations.cleanenergylien} onChange={(v) => onDeclaration("cleanenergylien", v)} /></Col></Row>
                </div>
            </div>

            <div id="5b">
                <div className="form1003Title">5b. About Your Finances</div>
                <div className="px-4 pt-4">
                    {[
                        ["F. Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?", "comakeronnote"],
                        ["G. Are there any outstanding judgments against you?", "judgements"],
                        ["H. Are you currently delinquent or in default on a Federal debt?", "delinquentondebt"],
                        ["I. Are you a party to a lawsuit in which you potentially have any personal financial liability?", "lawsuit"],
                        ["J. Have you conveyed title to any property in lieu of foreclosure in the past 7 years?", "conveyedtitle"],
                        ["K. Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due?", "loanforeclosure"],
                        ["L. Have you had property foreclosed upon in the last 7 years?", "propertyforeclosure"],
                        ["M. Have you declared bankruptcy within the past 7 years?", "bancrupt"],
                    ].map(([label, key], i) => (
                        <React.Fragment key={key}>
                            {i > 0 ? <hr className="form1003Divider" /> : null}
                            <Row className="mb-4"><Col className="px-0"><YesNo label={label} value={declarations[key]} onChange={(v) => onDeclaration(key, v)} /></Col></Row>
                        </React.Fragment>
                    ))}
                    {declarations.bancrupt === "yes" ? (
                        <Row className="mb-4">
                            <Col className="px-0">
                                <div className="bankruptcy-type-row">
                                    <div className="bankruptcy-type-label fontWeight500">Identify the type(s) of bankruptcy</div>
                                    <div className="bankruptcy-type-options">
                                        {[
                                            ["chapter7", "Chapter 7"],
                                            ["chapter11", "Chapter 11"],
                                            ["chapter12", "Chapter 12"],
                                            ["chapter13", "Chapter 13"],
                                        ].map(([s, label]) => (
                                            <ChromeCheckbox
                                                key={s}
                                                checked={!!declarations[s]}
                                                onChange={(e) => onDeclaration(s, e.target.checked)}
                                            >
                                                {label}
                                            </ChromeCheckbox>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    ) : null}
                </div>
            </div>

            <div id="s6">
                <div className="p-4">
                    <div className="fontWeight500" style={{ fontSize: 20 }}>Section 6: Acknowledgments and Agreements.</div>
                    <div>This section tells you about your legal obligations when you sign this application.</div>
                </div>
                <div className="form1003Title">Acknowledgments and Agreements</div>
                <div className="p-4">
                    <div className="fontWeight500 mb-3">Definitions:</div>
                    <li>"Lender" includes the Lender's agents, service providers, and any of their successors and assigns.</li>
                    <li>"Other Loan Participants" includes (i) any actual or potential owners of a loan resulting from this application (the "Loan"), (ii) acquirers of any beneficial or other interest in the Loan, (iii) any mortgage insurer, (iv) any guarantor, (v) any servicer of the Loan, and (vi) any of these parties' service providers, successors or assigns.</li>
                    <div className="fontWeight500 my-3">I agree to, acknowledge, and represent the following:</div>
                    <div className="fontWeight500 mb-3">(1) The Complete Information for this Application</div>
                    <li>The information I have provided in this application is true, accurate, and complete as of the date I signed this application.</li>
                    <li>If the information I submitted changes or I have new information before closing of the Loan, I must change and supplement this application, including providing any updated/supplemented real estate sales contract.</li>
                    <li>For purchase transactions: The terms and conditions of any real estate sales contract signed by me in connection with this application are true, accurate, and complete to the best of my knowledge and belief. I have not entered into any other agreement, written or oral, in connection with this real estate transaction</li>
                    <li>The Lender and Other Loan Participants may rely on the information contained in the application before and after closing of the Loan</li>
                    <li>Any intentional or negligent misrepresentation of information may result in the imposition of:</li>
                    <div>(a) civil liability on me, including monetary damages, if a person suffers any loss because the person relied on any misrepresentation that I have made on this application, and/or</div>
                    <div>(b) criminal penalties on me including, but not limited to, fine or imprisonment or both under the provisions of Federal law (18 U.S.C. §§ 1001 et seq.).</div>
                </div>
            </div>

            <div id="s7">
                <div className="p-4">
                    <div className="fontWeight500" style={{ fontSize: 20 }}>Section 7: Military Service</div>
                    <div>This section asks questions about your (or your deceased spouse's) military service.</div>
                </div>
                <div className="form1003Title">Military Service of Borrower</div>
                <div className="p-4">
                    <Row className="mb-4"><Col><YesNo label="Did you (or your deceased spouse) ever serve, or are you currently serving, in the United States Armed Forces?" value={declarations.servearmedforces} onChange={(v) => onDeclaration("servearmedforces", v)} /></Col></Row>
                    {declarations.servearmedforces === "yes" ? (
                        <div>
                            {[
                                ["currentlyserving", "Currently serving on active duty with projected expiration date of service/tour"],
                                ["currentlyretired", "Currently retired, discharged, or separated from service"],
                                ["nonactive", "Only period of service was as a non-activated member of the Reserve or National Guard"],
                                ["survivingspouse", "Surviving spouse"],
                            ].map(([key, label]) => (
                                <div className="mb-2" key={key} style={key === "currentlyserving" ? { display: "flex", flexWrap: "wrap", alignItems: "center" } : undefined}>
                                    <ChromeCheckbox checked={!!military[key]} onChange={(e) => setMilitary(key, e.target.checked)}>{label}</ChromeCheckbox>
                                    {key === "currentlyserving" && military.currentlyserving ? (
                                        <Input
                                            className="ml-3"
                                            style={{ width: 200, marginBottom: 10 }}
                                            placeholder="MM/DD/YYYY"
                                            value={military.projectedexpirationdate || ""}
                                            onChange={(e) => setMilitary("projectedexpirationdate", formatMilitaryDate(e.target.value))}
                                        />
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>

            <div id="s8">
                <div className="p-4">
                    <div className="fontWeight500" style={{ fontSize: 20 }}>Section 8: Demographic Information</div>
                    <div>This section asks about your ethnicity, sex, and race.</div>
                </div>
                <div className="form1003Title">Demographic Information of Borrower</div>
                <div className="p-4">
                    <Row className="mb-4">
                        <Col>
                            <Field label="Ethnicity">
                                <ChromeSelect style={{ width: "100%" }} value={eo.ethnicity || ""} options={[
                                    { value: "", label: "..." },
                                    { value: "hispanic", label: "Hispanic or Latino" },
                                    { value: "nonhispanic", label: "Not Hispanic or Latino" },
                                    { value: "donttell", label: "I do not wish to provide this information" },
                                ]} onChange={(v) => setEO({ ethnicity: v })} />
                            </Field>
                        </Col>
                    </Row>
                    {eo.ethnicity === "hispanic" ? (
                        <Row className="mb-4">
                            <Col>
                                <div className="fontWeight500 mb-3">Check all that apply:</div>
                                {[["mexican", "Mexican"], ["puertorican", "Puerto Rican"], ["cuban", "Cuban"], ["other", "Other Hispanic or Latino"]].map(([key, label]) => (
                                    <div className="mb-2" key={key} style={key === "other" ? { display: "flex", flexWrap: "wrap", alignItems: "center" } : undefined}>
                                        <ChromeCheckbox checked={!!eoHispanic[key]} onChange={(e) => setHispanic(key, e.target.checked)}>{label}</ChromeCheckbox>
                                        {key === "other" && eoHispanic.other ? (
                                            <Input className="ml-3" style={{ width: 240 }} placeholder="Chilean, Peruvian, and so on." value={eoHispanic.otherdesctiption || ""} onChange={(e) => setHispanic("otherdesctiption", e.target.value)} />
                                        ) : null}
                                    </div>
                                ))}
                            </Col>
                        </Row>
                    ) : null}
                    <Row className="mb-4">
                        <Col>
                            <Field label="Sex">
                                <ChromeSelect style={{ width: "100%" }} value={eo.sex || ""} options={[
                                    { value: "female", label: "Female" },
                                    { value: "male", label: "Male" },
                                    { value: "other", label: "Other" },
                                    { value: "donttell", label: "I do not wish to provide this information" },
                                ]} onChange={(v) => setEO({ sex: v })} />
                            </Field>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            <Field label="Race">
                                {[["americanindian", "American Indian or Alaska Native"], ["asian", "Asian"], ["black", "Black or African American"], ["pacific", "Native Hawaiian or Other Pacific Islander"], ["white", "White"], ["notfurnished", "I do not wish to provide this information"]].map(([key, label]) => (
                                    <div className="mb-3" key={key}>
                                        <ChromeCheckbox checked={!!eoRace[key]} onChange={(e) => setRace(key, e.target.checked)}>{label}</ChromeCheckbox>
                                        {key === "americanindian" && eoRace.americanindian ? (
                                            <div className="mt-2 d-flex align-items-center">
                                                <span className="mr-2">Print name of enrolled or principal tribe</span>
                                                <Input style={{ width: 220 }} value={eoRace.indiantribe || ""} onChange={(e) => setRace("indiantribe", e.target.value)} />
                                            </div>
                                        ) : null}
                                        {key === "asian" && eoRace.asian ? (
                                            <div className="mt-2 ml-4">
                                                {[["asianindian", "Asian Indian"], ["chinese", "Chinese"], ["filipino", "Filipino"], ["japanese", "Japanese"], ["korean", "Korean"], ["vietnamese", "Vietnamese"], ["otherasian", "Other Asian"]].map(([sub, subLabel]) => (
                                                    <div key={sub} className="mb-2" style={sub === "otherasian" ? { display: "flex", flexWrap: "wrap", alignItems: "center" } : undefined}>
                                                        <ChromeCheckbox
                                                            className="mr-3"
                                                            checked={!!eoRace.asiandetails?.[sub]}
                                                            onChange={(e) => setRace("asiandetails", { ...(eoRace.asiandetails || {}), [sub]: e.target.checked })}
                                                        >
                                                            {subLabel}
                                                        </ChromeCheckbox>
                                                        {sub === "otherasian" && eoRace.asiandetails?.otherasian ? (
                                                            <Input
                                                                style={{ width: 220 }}
                                                                placeholder="Hmong, Laotian, and so on..."
                                                                value={eoRace.asiandetails?.otherdesctiption || ""}
                                                                onChange={(e) => setRace("asiandetails", { ...(eoRace.asiandetails || {}), otherdesctiption: e.target.value })}
                                                            />
                                                        ) : null}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                        {key === "pacific" && eoRace.pacific ? (
                                            <div className="mt-2 ml-4">
                                                {[["hawaian", "Native Hawaiian"], ["guamanian", "Guamanian or Chamorro"], ["samoan", "Samoan"], ["other", "Other Pacific Islander"]].map(([sub, subLabel]) => (
                                                    <div key={sub} className="mb-2" style={sub === "other" ? { display: "flex", flexWrap: "wrap", alignItems: "center" } : undefined}>
                                                        <ChromeCheckbox
                                                            className="mr-3"
                                                            checked={!!eoRace.pacificdetails?.[sub]}
                                                            onChange={(e) => setRace("pacificdetails", { ...(eoRace.pacificdetails || {}), [sub]: e.target.checked })}
                                                        >
                                                            {subLabel}
                                                        </ChromeCheckbox>
                                                        {sub === "other" && eoRace.pacificdetails?.other ? (
                                                            <Input
                                                                style={{ width: 220 }}
                                                                placeholder="Fijian, Tongan, and so on..."
                                                                value={eoRace.pacificdetails?.otherdetails || ""}
                                                                onChange={(e) => setRace("pacificdetails", { ...(eoRace.pacificdetails || {}), otherdetails: e.target.value })}
                                                            />
                                                        ) : null}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </Field>
                        </Col>
                    </Row>
                </div>
            </div>

            <div id="s9">
                <div className="p-4">
                    <div className="fontWeight500" style={{ fontSize: 20 }}>Section 9: Loan Originator Information</div>
                    <div>To be completed by the loan originator.</div>
                </div>
                <div className="form1003Title">Loan Originator Information</div>
                <div className="p-4">
                    <Row>
                        <Col md={4} className="mb-4">
                            <TextField label="Loan originator organization name" value={application?.brokerInfo?.name} onChange={(v) => onBrokerInfo({ name: v })} />
                        </Col>
                        <Col md={4} className="mb-4">
                            <TextField label="Loan originator organization NMLSR ID" value={application?.brokerInfo?.nmls} onChange={(v) => onBrokerInfo({ nmls: v })} />
                        </Col>
                        <Col md={4} className="mb-4">
                            <TextField label="State license ID" value={application?.brokerInfo?.licenseid} onChange={(v) => onBrokerInfo({ licenseid: v })} />
                        </Col>
                    </Row>
                    <StructuredAddressField
                        id="v2-broker-address"
                        label="Address"
                        address={application?.brokerInfo}
                        onChange={(key, value) => onBrokerInfo({ [key]: value })}
                        onPlace={onBrokerPlace}
                        loanLimits={loanLimits}
                    />
                    <Row>
                        <Col md={4} className="mb-4">
                            <TextField label="Loan originator name" value={application?.brokerInfo?.loanofficername} onChange={(v) => onBrokerInfo({ loanofficername: v })} />
                        </Col>
                        <Col md={4} className="mb-4">
                            <TextField label="Loan originator NMLSR ID" value={application?.brokerInfo?.loanofficernmls} onChange={(v) => onBrokerInfo({ loanofficernmls: v })} />
                        </Col>
                        <Col md={4} className="mb-4">
                            <TextField label="Loan originator state license ID" value={application?.brokerInfo?.loanofficerlicenseid} onChange={(v) => onBrokerInfo({ loanofficerlicenseid: v })} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6} className="mb-4">
                            <TextField label="Email" value={application?.brokerInfo?.email} onChange={(v) => onBrokerInfo({ email: v })} />
                        </Col>
                        <Col md={6} className="mb-4">
                            <TextField
                                label="Phone"
                                value={com.fixPhoneInput(application?.brokerInfo?.phone)}
                                onChange={(v) => onBrokerInfo({ phone: com.fixPhoneInput(v) })}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </>
    )
}
